import { RubyGemMark } from '../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ChevronLeft, ChevronRight, CheckCircle2, FileSignature, PenLine, RefreshCw, ScrollText,
} from 'lucide-react';
import {
  BlockList, Button as UIButton, FieldLabel, IconCheck, IconHistory, IconPencil, IconSettings,
  IconX, PublishModal, Tag as UITag, VersionChain, VersionDiff, WarnNote as UIWarnNote,
  DangerNote as UIDangerNote, BlockEditor, dayToEffectiveAt, diffBlocks, summariseDiff,
  DeltaCompare, MergeFieldPalette, deltaOutline, visibleFieldsForContexts,
} from '@documents-ui';
import type {
  PillTab, PublishConfirmInput, DeltaEditorApi, DeltaCompareSummary,
} from '@documents-ui';
// Imported by SUBPATH, not from the barrel: DeltaEditor is the only
// Quill-bearing file in @documents-ui, and barrelling it made platform-app
// resolve and bundle Quill for screens with no documents on them. Quill's own
// stylesheet is imported here too — the app owns it, because the source-only
// package has no node_modules to resolve it from.
import { DeltaEditor } from '@documents-ui/DeltaEditor';
import 'react-quill-new/dist/quill.snow.css';
import {
  DeltaDocumentSchema as DeltaDocumentSchemaRaw,
  blocksToDelta as blocksToDeltaRaw,
  MERGE_CONTEXTS as MERGE_CONTEXTS_RAW,
  MERGE_CONTEXT_LABELS as MERGE_CONTEXT_LABELS_RAW,
  MERGE_FIELD_GROUPS as MERGE_FIELD_GROUPS_RAW,
  MERGE_FIELD_GROUP_LABELS as MERGE_FIELD_GROUP_LABELS_RAW,
  dischargeOf as dischargeOfRaw,
  dischargeConflict as dischargeConflictRaw,
  signatureBlockCount as signatureBlockCountRaw,
} from '@platform/shared-types';
import type {
  ChangeType, DeltaDocument, DocumentBlock, DocumentDischarge, DocumentKind, Folder, MergeContext,
} from '@platform/shared-types';
import { StaffPage, StaffSubtabs } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { documentsApi } from '../../services/documentsApi';
import { useDocumentCategories } from './documentCategories';
import { folderOptions } from './rubyChrome';
import { documentsExtApi } from '../../services/documentsExtApi';
import { SignaturePad } from '@documents-ui';
import type { SignatureResult } from '@documents-ui';
import type {
  DocumentDetail as DocumentDetailPayload, EnvelopeDetail, EnvelopeRow, PublishGateFailure,
  VersionArtifactRef, VersionAuditEntry, VersionSummary,
} from '../../services/documentsApi';
import {
  activeVersionRow, documentHeadState, ENVELOPE_STATUS_LABEL, ENVELOPE_STATUS_TONE, formatUtcDay,
  formatUtcStamp, headVersion, openVersion, previousVersionNo, VERSION_STATUS_LABEL,
} from './legalFormat';
import { mapPool, POOL } from './mapPool';
import { AT_NOW, canPublish, IDLE_RADIUS, radiusAnswers, radiusCountFor } from './radiusGuard';
import type { EffectiveInstant, RadiusState } from './radiusGuard';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * LEGAL / POLICIES / {document} — mockup screens 2, 3 and 4.
 *
 * Editor · Versions · Acceptance · Settings, on top of the shared
 * @documents-ui components. Everything on this page that looks like a status
 * is DERIVED: `activeVersionNo`, `isActive`, `isScheduled` and `isSuperseded`
 * come back computed from the chain on every read. Nothing stores them and
 * nothing here re-derives or caches them.
 *
 * Effective dates render in UTC (see legalFormat.ts) — a legal date rendered
 * browser-local shows the previous day to every US reader.
 *
 * CHROME: RUBY light (StaffPage — black header bar + ruby underline +
 * stone-100 content with white cards), matching the two list pages.
 */

/* ── RUBY light-chrome adapters over the shared @documents-ui primitives ──
 *
 * RESTYLE ONLY: same props, same behaviour. The shared package still ships its
 * dark defaults, so these thin wrappers repaint them with `!` overrides for
 * this light page. If/when @documents-ui itself goes light these become no-ops
 * with identical class values. */

const BTN_LIGHT: Record<string, string> = {
  default: '!border-stone-300 !bg-white !text-stone-700 hover:!bg-stone-50',
  primary: '!border-admin-primary !bg-admin-primary !text-white hover:!border-admin-secondary hover:!bg-admin-secondary',
  admin: '!border-admin-primary !bg-admin-primary !text-white hover:!border-admin-secondary hover:!bg-admin-secondary',
  danger: '!border-red-600 !bg-red-600 !text-white hover:!border-red-700 hover:!bg-red-700',
};

function Button({ className = '', ...rest }: ComponentProps<typeof UIButton>) {
  return <UIButton {...rest} className={`${BTN_LIGHT[rest.variant ?? 'default']} ${className}`} />;
}

const TAG_LIGHT: Record<string, string> = {
  published: '!bg-emerald-100 !text-emerald-800',
  active: '!bg-emerald-100 !text-emerald-800',
  draft: '!bg-stone-100 !text-stone-600',
  minor: '!bg-stone-100 !text-stone-600',
  review: '!bg-amber-100 !text-amber-800',
  approved: '!bg-blue-100 !text-blue-800',
  scheduled: '!bg-rose-100 !text-rose-800',
  retired: '!bg-red-100 !text-red-800',
  material: '!bg-red-100 !text-red-800',
};

function Tag({ className = '', ...rest }: ComponentProps<typeof UITag>) {
  return <UITag {...rest} className={`${TAG_LIGHT[rest.tone] ?? ''} ${className}`} />;
}

function WarnNote({ className = '', ...rest }: ComponentProps<typeof UIWarnNote>) {
  return <UIWarnNote {...rest} className={`!border-amber-300 !bg-amber-50 !text-amber-800 ${className}`} />;
}

function DangerNote({ className = '', ...rest }: ComponentProps<typeof UIDangerNote>) {
  return <UIDangerNote {...rest} className={`!border-red-300 !bg-red-50 !text-red-700 ${className}`} />;
}

const INPUT_CLASS =
  'w-full min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] text-stone-900 placeholder:text-stone-400 focus:border-admin-primary focus:outline-none focus:ring-1 focus:ring-admin-primary';

/**
 * `DeltaDocumentSchema` is a RUNTIME value from `@platform/shared-types`, and a
 * Vite dep pre-bundle built before it existed hands back `undefined`. Reading
 * `.safeParse` off that would throw at SAVE TIME — the worst moment — with a
 * message naming neither the cause nor the fix.
 *
 * So it is resolved through a guard that fails loudly and, crucially, names the
 * actual remedy: RESTART the dev server. Deleting `node_modules/.vite` under a
 * running server does nothing; Vite keeps the optimised bundle in memory.
 */
function deltaSchema(): typeof DeltaDocumentSchemaRaw | null {
  if (DeltaDocumentSchemaRaw && typeof (DeltaDocumentSchemaRaw as any).safeParse === 'function') {
    return DeltaDocumentSchemaRaw;
  }
  console.error(
    '@platform/shared-types exported no DeltaDocumentSchema — a stale Vite pre-bundle. '
    + 'RESTART the dev server: deleting node_modules/.vite under a running server does nothing.',
  );
  return null;
}

/**
 * The context list and its labels are RUNTIME values from
 * `@platform/shared-types`, so they get the same treatment as the schema above:
 * a stale Vite pre-bundle hands back `undefined`, and mapping over that would
 * blank the Settings tab rather than say why. Falling back to an empty list
 * renders no chips and logs the remedy — the rest of the tab keeps working.
 */
/**
 * Guarded like every other runtime shared-types import on this screen: a stale
 * dist resolves a new export to `undefined`, and calling it would blank the
 * settings panel instead of naming the rebuild.
 */
const dischargeOf: typeof dischargeOfRaw = typeof dischargeOfRaw === 'function'
  ? dischargeOfRaw
  : (d) => ((d?.discharge === 'signature' || d?.discharge === 'accept_decline')
    ? d.discharge
    : (d?.kind === 'agreement' ? 'signature' : 'accept_decline'));

const dischargeConflict: typeof dischargeConflictRaw = typeof dischargeConflictRaw === 'function'
  ? dischargeConflictRaw
  : () => null;
const signatureBlockCount: typeof signatureBlockCountRaw = typeof signatureBlockCountRaw === 'function'
  ? signatureBlockCountRaw
  : () => 0;

const MERGE_CONTEXTS: readonly MergeContext[] = Array.isArray(MERGE_CONTEXTS_RAW)
  ? MERGE_CONTEXTS_RAW
  : [];
const MERGE_CONTEXT_LABELS: Record<string, string> = MERGE_CONTEXT_LABELS_RAW ?? {};
const MERGE_FIELD_GROUPS: readonly string[] = Array.isArray(MERGE_FIELD_GROUPS_RAW)
  ? MERGE_FIELD_GROUPS_RAW
  : [];
const MERGE_FIELD_GROUP_LABELS: Record<string, string> = MERGE_FIELD_GROUP_LABELS_RAW ?? {};
if (!MERGE_CONTEXTS.length) {
  console.error(
    '@platform/shared-types exported no MERGE_CONTEXTS — a stale Vite pre-bundle. '
    + 'RESTART the dev server: deleting node_modules/.vite under a running server does nothing.',
  );
}

/**
 * A LEGACY BLOCK DOCUMENT, AS ONE CONTINUOUS DOCUMENT.
 *
 * Every document opens in the continuous editor, including the 76 authored
 * before it existed. The conversion happens HERE, IN MEMORY, on read — the
 * stored version is not touched, and nothing is written until the author saves
 * a draft. A published version therefore keeps rendering from the exact blocks
 * its signature hash was computed over.
 *
 * `blocksToDelta` is the same function the 76-document equivalence gate
 * verifies (`documents-ext-service`), which renders both forms and requires
 * byte-identical HTML. That gate is the reason converting on open is safe
 * rather than a guess.
 *
 * Returns `undefined` when the blocks cannot be represented, so the caller
 * shows a real error instead of an empty page pretending to be the document.
 */
/**
 * An empty document, in the exact shape Quill produces. A Delta must end with
 * a newline — line attributes ride on the newline that terminates their line —
 * so the empty document is one newline, never `[]`.
 */
const EMPTY_DELTA: DeltaDocument = { ops: [{ insert: '\n' }] } as DeltaDocument;

function asDelta(blocks: readonly DocumentBlock[]): DeltaDocument | undefined {
  if (typeof blocksToDeltaRaw !== 'function') {
    console.error(
      '@platform/shared-types exported no blocksToDelta — a stale Vite pre-bundle. '
      + 'RESTART the dev server: deleting node_modules/.vite under a running server does nothing.',
    );
    return undefined;
  }
  if (!blocks.length) return undefined;
  try {
    return blocksToDeltaRaw(blocks);
  } catch (e: any) {
    console.error('[DocumentDetail] blocks → delta conversion failed', e);
    return undefined;
  }
}

/** Light modal card — same structure and behaviour as @documents-ui ModalShell. */
function ModalShell({
  title, subtitle, children, footerNote, footerActions, onClose, labelledById,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footerNote?: ReactNode;
  footerActions?: ReactNode;
  onClose: () => void;
  labelledById?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className="w-full max-w-[620px] rounded-2xl border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-stone-200 px-5 py-4">
          <div className="flex-1">
            <h3 id={labelledById} className="text-base font-semibold text-stone-900">{title}</h3>
            {subtitle && <p className="mt-1 text-[12.5px] text-stone-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            <IconX size={18} />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {(footerNote || footerActions) && (
          <div className="flex flex-col gap-3 border-t border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[11.5px] text-stone-500">{footerNote}</span>
            <div className="flex gap-2">{footerActions}</div>
          </div>
        )}
      </div>
    </div>
  );
}

type TabId = 'document' | 'editor' | 'versions' | 'acceptance' | 'envelopes' | 'settings';

/**
 * THE PUBLISH GATE'S 409, MADE READABLE. `gateVersion()` in documents-service
 * returns one failure per (field, context) pair it refused — a field written
 * into the document whose provider is not available in this document's
 * context (spec §7 "structural", the same class canonicalizeAlias/
 * classifyField compute for the merge-render path). Each line names the
 * field exactly as written, the reason (why — reused verbatim from the
 * server, which already names the provider and the context), and a remedy
 * (what to do about it). Multiple failures are just multiple lines; nothing
 * here is truncated, because a publish that fails on 3 fields and reports 1
 * sends the author back for a second rejected publish.
 */
function formatGateFailures(failures: readonly PublishGateFailure[]): string {
  return failures
    .map((f) => `"${f.field}" — ${f.reason} Remove the field, or change this document's context.`)
    .join('  ');
}

/**
 * THE THIRD TAB IS DECIDED BY `kind`, AND IT IS NOT COSMETIC.
 *
 * A POLICY is ACCEPTED: the acceptance ledger, the blast radius and the
 * re-acceptance gate all describe real things that happen to it.
 *
 * An AGREEMENT is never accepted. It is instantiated into an ENVELOPE — one
 * copy of one published version, with its merge data frozen, addressed to
 * named signers — and signed. Showing an agreement an acceptance ledger
 * measures a thing that cannot happen to it, and a 0 there does not mean
 * "nobody accepted", it means "acceptance is not how this document works" —
 * which is the reading a number can never carry.
 */
function tabsFor(kind: DocumentKind | undefined): PillTab[] {
  return [
    // The PUBLISHED artifact comes first: it is what an operator prints,
    // downloads and sends for a wet-ink signature, and it is the real stored
    // file rather than a re-render of it.
    { id: 'document', label: 'Document', icon: <ScrollText className="h-3.5 w-3.5" /> },
    { id: 'editor', label: 'Edit', icon: <IconPencil size={14} /> },
    { id: 'versions', label: 'Versions', icon: <IconHistory size={14} /> },
    kind === 'agreement'
      ? { id: 'envelopes', label: 'Envelopes', icon: <FileSignature className="h-3.5 w-3.5" /> }
      : { id: 'acceptance', label: 'Acceptance', icon: <IconCheck size={14} /> },
    { id: 'settings', label: 'Settings', icon: <IconSettings size={14} /> },
  ];
}

/*
 * A blast-radius answer is only valid for the exact question it was asked.
 * `RadiusState.forChangeType` / `forEffectiveAt` record that question, so the
 * page can tell "0 acceptances go stale" apart from "we have a 0 lying around
 * that answers a different change type or a different effective date".
 *
 * `forEffectiveAt` is an ISO instant, or the `AT_NOW` sentinel when the number
 * was computed against the current instant (the Acceptance tab's button). It is
 * never `null` for a real answer — `null` there means "nothing fetched yet",
 * and `pendingEffectiveAt` is `null` until a day is picked, so the two would
 * otherwise compare equal and an at-now number would pass as the answer to a
 * dateless publish modal.
 *
 * The decision itself — does this number answer THAT question, and may a
 * material change publish against it — lives in ./radiusGuard.ts as pure
 * functions, so it is unit-tested without a DOM. Nothing here re-implements it.
 */

/** How long to sit on a radio/date change before asking the server again. */
const RADIUS_DEBOUNCE_MS = 250;

/**
 * The line under the blast-radius number inside the publish modal.
 *
 * This replaces the old `MaterialRadiusProbe`, which abused the mount/unmount of
 * the modal's material-only branch as a "the radio changed" signal because the
 * component exposed no callback. `PublishModal` now reports `changeType` and
 * `effectiveDay` upward, so this is plain presentation again — no effects, no
 * hidden control flow.
 */
function RadiusDetail({
  state, changeType, hasDay, answersSelection,
}: {
  state: RadiusState;
  changeType: ChangeType | null;
  hasDay: boolean;
  answersSelection: boolean;
}) {
  if (!hasDay) {
    return (
      <>
        Pick an effective date first — the count is evaluated at that date, not at today, so a
        future-dated publish answers a different question.
      </>
    );
  }
  if (state.loading || !answersSelection) {
    if (state.error && !state.loading) {
      return (
        <>
          Blast radius could not be computed (<b>{state.error}</b>). Publishing a material change is
          blocked until it can be — it must never ship against an unknown number.
        </>
      );
    }
    return <>Counting acceptances against the selected change type and effective date…</>;
  }
  return (
    <>
      {changeType === 'material'
        ? 'Affected parties hit a blocking “Review and accept the updated document” screen at next login.'
        : 'Nobody goes stale because of this publish. Anyone counted was already stale from an earlier material version, and a minor publish does not clear them.'}
      {state.total !== null && (
        <span className="text-stone-500"> {state.total} acceptance{state.total === 1 ? '' : 's'} on record.</span>
      )}
    </>
  );
}

/**
 * The lifecycle story of one version, oldest first, straight from the
 * append-only audit table. A REJECTION renders its reason inline — the reason
 * is mandatory server-side precisely so this list is reviewable months later.
 */
function VersionTimeline({ entries }: { entries: VersionAuditEntry[] | null | undefined }) {
  // Three-valued on purpose: undefined = still fetching, null = the service
  // could not read the audit table, [] = a real "nothing recorded".
  if (entries === undefined) {
    return <RubyLoader variant="section" label="Loading history…" />;
  }
  if (entries === null) {
    return (
      <p className="text-[12.5px] text-stone-500">
        History unavailable — the audit table could not be read. The version itself is unaffected.
      </p>
    );
  }
  if (!entries.length) {
    return <p className="text-[12.5px] text-stone-500">Nothing recorded yet.</p>;
  }
  const label = (e: VersionAuditEntry): string => {
    const d = (e.detail ?? {}) as Record<string, unknown>;
    switch (e.action) {
      case 'version.draft_created': return 'Draft created';
      case 'version.submitted': return 'Submitted for review';
      case 'version.approved': return d.selfApproved ? 'Approved (self-approved)' : 'Approved';
      case 'version.rejected':
        return d.from === 'approved' ? 'Approval withdrawn — back to draft' : 'Rejected — back to draft';
      case 'version.published': {
        const eff = typeof d.effectiveAt === 'string' ? ` · effective ${formatUtcDay(d.effectiveAt)}` : '';
        const ct = typeof d.changeType === 'string' ? ` (${d.changeType})` : '';
        return `Published${ct}${eff}`;
      }
      default: return e.action ?? 'unknown event';
    }
  };
  return (
    <ol className="space-y-3">
      {entries.map((e, i) => {
        const reason = (e.detail as Record<string, unknown> | null)?.reason;
        const rejected = e.action === 'version.rejected';
        return (
          <li key={`${e.at}-${i}`} className="relative pl-4">
            <span
              aria-hidden
              className={`absolute left-0 top-[5px] h-2 w-2 rounded-full ${
                rejected ? 'bg-red-500' : e.action === 'version.published' ? 'bg-emerald-500' : 'bg-stone-400'}`}
            />
            <p className={`text-[12.5px] ${rejected ? 'text-red-700' : 'text-stone-800'}`}>{label(e)}</p>
            <p className="text-[11px] text-stone-500">
              {formatUtcStamp(e.at ?? undefined)}{e.actorId ? ` · ${e.actorId}` : ''}
            </p>
            {rejected && typeof reason === 'string' && reason && (
              <p className="mt-1 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[12px] leading-relaxed text-red-700">
                {reason}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function DocumentDetailPage() {
  const { documentId = '' } = useParams();
  const navigate = useNavigate();

  /**
   * The same editor serves both kinds, reached at /legal/policies/{id} and
   * /legal/agreements/{id}. The list to go back to comes from the ROUTE, not
   * from `document.kind`: it is "where you came from", it is known before the
   * document loads so the breadcrumb never flickers, and Policies currently
   * lists documents of both kinds.
   */
  const parentList = useLocation().pathname.startsWith('/legal/agreements')
    ? { path: '/legal/agreements', label: 'Agreements' }
    : { path: '/legal/policies', label: 'Policies' };

  const [detail, setDetail] = useState<DocumentDetailPayload | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('document');
  const [busy, setBusy] = useState(false);

  /** versionNo → blocks. Published versions are immutable, so this never goes stale. */
  const [blocksByVersion, setBlocksByVersion] = useState<Record<number, DocumentBlock[]>>({});
  /**
   * versionNo → lifecycle timeline. UNLIKE blocks, an OPEN version's history
   * grows (submitted, rejected, approved…), so the Versions tab re-reads it for
   * open versions rather than trusting this cache. `null` = the service could
   * not read the audit table ("unavailable"), which must never render as [].
   */
  const [auditByVersion, setAuditByVersion] = useState<Record<number, VersionAuditEntry[] | null>>({});
  const [draftBlocks, setDraftBlocks] = useState<DocumentBlock[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * versionNo → the continuous-document body, for versions that have one.
   *
   * Kept beside `blocksByVersion` rather than replacing it: 76 stored
   * documents are still block documents and must keep opening. A version has
   * one body or the other, never both — `delta` wins wherever it exists.
   */
  const [deltaByVersion, setDeltaByVersion] = useState<Record<number, DeltaDocument | undefined>>({});
  /** versionNo → frozen artifacts. Published versions are immutable, so this never goes stale. */
  const [artifactsByVersion, setArtifactsByVersion] =
    useState<Record<number, VersionArtifactRef[] | undefined>>({});
  const [draftDelta, setDraftDelta] = useState<DeltaDocument | null>(null);
  /** Set once the author has typed, so an untouched draft never saves. */
  const [deltaDirty, setDeltaDirty] = useState(false);
  const editorApi = useRef<DeltaEditorApi | null>(null);
  /** Editor ↔ redline toggle inside the Editor tab. */
  const [showChanges, setShowChanges] = useState(false);
  /** Which version the redline compares AGAINST — "like git diff", any two. */
  const [compareAgainst, setCompareAgainst] = useState<number | null>(null);

  /* ── envelopes (agreements only) ───────────────────────────────────────
   *
   * `envRows === null` means "not fetched", NOT "none": an empty array is the
   * real answer "this template has no envelopes yet", and the tab says exactly
   * that rather than looking broken.
   *
   * `envDetails[id]` is three-valued for the same reason — `undefined` while
   * the detail read is in flight, `null` when it failed, the payload when it
   * landed. Signers are NOT on the list row, so the only way to show them is
   * one detail read per envelope, and a failed read must not render as
   * "0 signers" when 0 signers is itself a meaningful state (an envelope with
   * no signers can never be sent).
   */
  const [envRows, setEnvRows] = useState<EnvelopeRow[] | null>(null);
  const [envDetails, setEnvDetails] = useState<Record<string, EnvelopeDetail | null>>({});
  const [envLoading, setEnvLoading] = useState(false);
  const [envError, setEnvError] = useState<string | null>(null);
  const [openEnvelopeId, setOpenEnvelopeId] = useState<string | null>(null);
  /** Monotonic request id — a slow reply for an abandoned load is dropped. */
  const envSeq = useRef(0);

  const [selectedVersionNo, setSelectedVersionNo] = useState<number | null>(null);
  /**
   * The send-back modal serves BOTH edges of the same endpoint: a reviewer
   * REJECTING an in_review version, and an admin UN-APPROVING an approved one
   * before it is published. Which copy to show is decided by the version's
   * status at open time, not by two parallel modals.
   */
  const [sendBackFor, setSendBackFor] = useState<VersionSummary | null>(null);
  const [sendBackReason, setSendBackReason] = useState('');
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [radius, setRadius] = useState<RadiusState>(IDLE_RADIUS);
  const [retireOpen, setRetireOpen] = useState(false);

  /*
   * The publish modal still OWNS its change type and effective day; these are
   * the mirrored copies it reports up via onChangeTypeChange /
   * onEffectiveDayChange. They exist for one reason: the blast radius must be
   * re-computed for the choice on screen, and a future-dated publish must be
   * evaluated at the date chosen rather than at now.
   */
  const [pendingChangeType, setPendingChangeType] = useState<ChangeType | null>(null);
  const [pendingDay, setPendingDay] = useState<string>('');
  /** Monotonic request id — a slow reply for an older question is dropped. */
  const radiusSeq = useRef(0);

  const nowIso = useMemo(() => new Date().toISOString(), [detail]);

  const versions = detail?.versions ?? [];
  const open = useMemo(() => openVersion(versions), [versions]);
  const head = useMemo(() => headVersion(versions), [versions]);
  const active = useMemo(
    () => (detail ? activeVersionRow(versions, detail.activeVersionNo) : null),
    [detail, versions],
  );
  const state = detail ? documentHeadState(detail.document.status, versions) : null;

  const fetchBlocks = useCallback(async (
    versionNo: number,
  ): Promise<{ blocks: DocumentBlock[]; delta?: DeltaDocument }> => {
    const res = await documentsApi.getVersion(documentId, versionNo);
    const blocks = (res.version?.blocks ?? []) as DocumentBlock[];
    /*
     * Stored Delta if the version has one; otherwise the blocks converted in
     * memory. EVERY document opens in the continuous editor — the block
     * structure is never shown. See `asDelta`: nothing is written by this.
     */
    const stored = (res.version as any)?.delta as DeltaDocument | undefined;
    const delta = stored ?? asDelta(blocks);
    setBlocksByVersion((m) => ({ ...m, [versionNo]: blocks }));
    setDeltaByVersion((m) => ({ ...m, [versionNo]: delta }));
    setArtifactsByVersion((m) => ({
      ...m,
      // `[]` is a real answer — a draft has no artifacts — and must render
      // differently from `undefined`, which means "not fetched yet".
      [versionNo]: ((res.version as any)?.artifacts ?? []) as VersionArtifactRef[],
    }));
    // The same GET carries the version's audit timeline — store it alongside.
    // `undefined` (older service build) is recorded as null: "unavailable".
    setAuditByVersion((m) => ({ ...m, [versionNo]: res.audit ?? null }));
    return { blocks, delta };
  }, [documentId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, f] = await Promise.all([
        documentsApi.getDocument(documentId),
        documentsApi.listFolders().catch(() => ({ folders: [] as Folder[], count: 0, invalid: [] })),
      ]);
      setDetail(d);
      setFolders(f.folders || []);

      const openV = openVersion(d.versions);
      const focus = openV ?? activeVersionRow(d.versions, d.activeVersionNo) ?? headVersion(d.versions);
      setSelectedVersionNo(focus ? focus.versionNo : null);

      if (focus) {
        const { blocks, delta } = await fetchBlocks(focus.versionNo);
        const isOpenDraft = !!openV && openV.versionNo === focus.versionNo && openV.status === 'draft';
        if (isOpenDraft) {
          setDraftBlocks(blocks);
          /*
           * EMPTY_DELTA ONLY WHEN THE VERSION IS GENUINELY EMPTY.
           *
           * `delta` is undefined for two very different reasons: the version
           * has no content at all, or it HAS content that could not be
           * converted. Treating both as "start with a blank page" would put a
           * real contract's author in front of an empty document, and the
           * first save would replace the contract with nothing.
           *
           * So a version that has blocks but no usable delta gets `null`,
           * which renders the conversion-failed notice instead of an editor.
           */
          setDraftDelta(delta ?? (blocks.length ? null : EMPTY_DELTA));
          setDirty(false);
          setDeltaDirty(false);
        } else {
          setDraftBlocks(null);
          setDraftDelta(null);
        }
        const prevNo = previousVersionNo(d.versions, focus.versionNo);
        // The redline defaults to the version immediately before, which is the
        // question a reviewer asks first. Any other version can be picked.
        setCompareAgainst(prevNo ?? null);
        setShowChanges(false);
        if (prevNo) await fetchBlocks(prevNo).catch(() => null);
      }
    } catch (e: any) {
      setError(e?.message || 'document_unavailable');
    } finally {
      setLoading(false);
    }
  }, [documentId, fetchBlocks]);

  useEffect(() => { void load(); }, [load]);

  /* ── envelopes ────────────────────────────────────────────────────────── */

  const isAgreement = detail?.document.kind === 'agreement';
  const tabs = useMemo(() => tabsFor(detail?.document.kind), [detail?.document.kind]);

  /**
   * The two kinds do not share a third tab, so a tab id that no longer exists
   * must be corrected rather than left selected — otherwise the pill row shows
   * one tab active and the panel below renders nothing at all.
   */
  useEffect(() => {
    if (!detail) return;
    if (isAgreement && tab === 'acceptance') setTab('envelopes');
    if (!isAgreement && tab === 'envelopes') setTab('acceptance');
  }, [detail, isAgreement, tab]);

  const loadEnvelopes = useCallback(async () => {
    const seq = envSeq.current + 1;
    envSeq.current = seq;
    setEnvLoading(true);
    setEnvError(null);
    try {
      const res = await documentsApi.listEnvelopes({ templateDocumentId: documentId });
      if (seq !== envSeq.current) return;
      const rows = res.envelopes || [];
      setEnvRows(rows);
      setEnvDetails({});

      /*
       * One detail read per envelope, through the shared bounded pool. The
       * list projection carries no signers and no signer count, so there is
       * nothing cheaper to read; what the pool buys is that a template with
       * forty envelopes does not open forty sockets. Each read is caught on
       * its own — one rejection escaping would lose every signer list,
       * including the ones that arrived.
       */
      const fetched = await mapPool<EnvelopeRow, EnvelopeDetail | null>(rows, POOL, async (r) => {
        try {
          return await documentsApi.getEnvelope(r.envelopeId);
        } catch {
          return null;
        }
      });
      if (seq !== envSeq.current) return;
      const next: Record<string, EnvelopeDetail | null> = {};
      rows.forEach((r, i) => { next[r.envelopeId] = fetched[i]; });
      setEnvDetails(next);
    } catch (e: any) {
      if (seq !== envSeq.current) return;
      setEnvRows(null);
      setEnvError(e?.message || 'envelope_list_failed');
    } finally {
      if (seq === envSeq.current) setEnvLoading(false);
    }
  }, [documentId]);

  /** Fetched when the tab is first opened, not on every page load. */
  useEffect(() => {
    if (tab !== 'envelopes') return;
    if (envRows !== null || envLoading || envError) return;
    void loadEnvelopes();
  }, [tab, envRows, envLoading, envError, loadEnvelopes]);

  /* ── the version the Versions tab is comparing ────────────────────────── */

  const comparedNo = selectedVersionNo ?? head?.versionNo ?? null;
  const comparedPrevNo = comparedNo ? previousVersionNo(versions, comparedNo) : null;

  useEffect(() => {
    if (tab !== 'versions' || !comparedNo) return;
    // `auditByVersion` missing counts too: blocks may be cached from the editor
    // while the timeline was never fetched for this version.
    const missing = [comparedNo, comparedPrevNo].filter(
      (n): n is number => !!n
        && (blocksByVersion[n] === undefined || auditByVersion[n] === undefined),
    );
    missing.forEach((n) => { void fetchBlocks(n).catch(() => null); });
  }, [tab, comparedNo, comparedPrevNo, blocksByVersion, auditByVersion, fetchBlocks]);

  /* ── editing ──────────────────────────────────────────────────────────── */

  const editorPrevNo = open ? previousVersionNo(versions, open.versionNo) : null;
  const editorPrevBlocks = editorPrevNo ? blocksByVersion[editorPrevNo] : undefined;

  const changedBlockCount = useMemo(() => {
    if (!open || !editorPrevBlocks) return undefined;
    const current = draftBlocks ?? blocksByVersion[open.versionNo] ?? [];
    const s = summariseDiff(diffBlocks(editorPrevBlocks, current));
    return s.changed + s.added + s.removed;
  }, [open, editorPrevBlocks, draftBlocks, blocksByVersion]);

  const saveBlocks = useCallback(async () => {
    if (!open || open.status !== 'draft' || !draftBlocks) return;
    setSaving(true);
    try {
      const res = await documentsApi.putBlocks(documentId, open.versionNo, draftBlocks);
      const saved = (res.version?.blocks ?? draftBlocks) as DocumentBlock[];
      setDraftBlocks(saved);
      setBlocksByVersion((m) => ({ ...m, [open.versionNo]: saved }));
      setDirty(false);
      toastService.success(`Draft v${open.versionNo} saved`);
    } catch (e: any) {
      toastService.error(`Could not save draft: ${e?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  }, [documentId, open, draftBlocks]);

  /**
   * Save the continuous document.
   *
   * VALIDATED HERE, BEFORE THE REQUEST. `DeltaDocumentSchema` is `.strict()`,
   * so an attribute the editor should never have been able to produce fails
   * the parse — and the author is told which one, on the spot, instead of the
   * whole save 400-ing with an issue list. A failure here means the editor's
   * format whitelist and the schema have drifted, which is worth knowing
   * loudly rather than papering over.
   */
  const saveDelta = useCallback(async () => {
    if (!open || open.status !== 'draft' || !draftDelta) return;
    const schema = deltaSchema();
    if (!schema) {
      toastService.error('Cannot validate the document — restart the dev server (see console).');
      return;
    }
    const parsed = schema.safeParse(draftDelta);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      toastService.error(
        `This document cannot be saved: ${first?.message ?? 'invalid document'}`
        + `${first?.path?.length ? ` (at ${first.path.join('.')})` : ''}`,
      );
      return;
    }
    setSaving(true);
    try {
      const res = await documentsApi.putDelta(documentId, open.versionNo, parsed.data);
      const saved = ((res.version as any)?.delta ?? parsed.data) as DeltaDocument;
      setDraftDelta(saved);
      setDeltaByVersion((m) => ({ ...m, [open.versionNo]: saved }));
      setBlocksByVersion((m) => ({ ...m, [open.versionNo]: [] }));
      setDeltaDirty(false);
      toastService.success(`Draft v${open.versionNo} saved`);
    } catch (e: any) {
      toastService.error(`Could not save draft: ${e?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  }, [documentId, open, draftDelta]);

  /**
   * Which body this draft is being edited as.
   *
   * A draft branched from a Delta version has one; a draft branched from one
   * of the 76 legacy block documents does not, and keeps the block editor
   * until someone converts it. Nothing converts silently — that decision
   * belongs to a human, because it rewrites how the document is stored.
   */
  const editingDelta = draftDelta !== null;
  const isDirty = editingDelta ? deltaDirty : dirty;
  const saveDraft = editingDelta ? saveDelta : saveBlocks;

  /* ── lifecycle ────────────────────────────────────────────────────────── */

  const runAction = useCallback(async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toastService.success(ok);
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'action_failed');
    } finally {
      setBusy(false);
    }
  }, [load]);

  const confirmSendBack = useCallback(async () => {
    if (!sendBackFor) return;
    const reason = sendBackReason.trim();
    if (!reason) return; // button is disabled; belt and braces
    const wasApproved = sendBackFor.status === 'approved';
    await runAction(
      () => documentsApi.reject(documentId, sendBackFor.versionNo, reason),
      wasApproved
        ? `v${sendBackFor.versionNo} un-approved — back to draft`
        : `v${sendBackFor.versionNo} rejected — back to draft`,
    );
    setSendBackFor(null);
    setSendBackReason('');
  }, [sendBackFor, sendBackReason, runAction, documentId]);

  const loadRadius = useCallback(async (changeType: ChangeType, effectiveAt?: string) => {
    if (!open) return;
    const seq = ++radiusSeq.current;
    /*
     * No date supplied means the server evaluates staleness at NOW — the
     * Acceptance tab's "Check blast radius" button. Record that as the AT_NOW
     * sentinel and never as `null`: `null` in this field means "no answer yet",
     * and the publish modal's pending instant is `null` until a day is picked,
     * so storing `null` here would let an at-now number pass as the answer to a
     * dateless publish modal. See radiusGuard.ts.
     */
    const asked: EffectiveInstant = effectiveAt ?? AT_NOW;
    setRadius((r) => ({ ...r, loading: true, error: null }));
    try {
      const r = await documentsApi.blastRadius(documentId, open.versionNo, changeType, effectiveAt);
      // A reply that arrives after a newer question was asked answers nothing.
      if (seq !== radiusSeq.current) return;
      setRadius({
        loading: false, count: r.staleAcceptanceCount, total: r.totalAcceptances,
        error: null, forChangeType: changeType, forEffectiveAt: asked,
      });
    } catch (e: any) {
      if (seq !== radiusSeq.current) return;
      setRadius({
        loading: false, count: null, total: null,
        error: e?.message || 'blast_radius_failed', forChangeType: changeType, forEffectiveAt: asked,
      });
    }
  }, [documentId, open]);

  /** The instant the pending selection would take effect, or null if undecided. */
  const pendingEffectiveAt = pendingDay ? dayToEffectiveAt(pendingDay) : null;

  /**
   * Re-fetch whenever EITHER the radio or the date changes, debounced so
   * clicking through the calendar does not fire a request per day. Both inputs
   * are required: without a change type there is no question to ask, and
   * without a date the server would silently evaluate at `now`, which is the
   * very bug this wiring exists to close.
   */
  useEffect(() => {
    if (!publishOpen || !open) return undefined;
    if (!pendingChangeType || !pendingEffectiveAt) return undefined;
    const t = setTimeout(() => {
      void loadRadius(pendingChangeType, pendingEffectiveAt);
    }, RADIUS_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [publishOpen, open, pendingChangeType, pendingEffectiveAt, loadRadius]);

  /** True only when `radius` answers exactly the selection now on screen. */
  const radiusAnswersSelection = radiusAnswers(radius, pendingChangeType, pendingEffectiveAt);

  const openPublish = useCallback(() => {
    setPublishError(null);
    setRadius(IDLE_RADIUS);
    radiusSeq.current += 1;   // abandon any in-flight answer from a previous open
    setPendingChangeType(null);
    setPendingDay('');
    setPublishOpen(true);
  }, []);

  const confirmPublish = useCallback(async (input: PublishConfirmInput) => {
    if (!open) return;
    // Refuse to publish a material change against a number we could not
    // compute, or one that answers a different question than the one on screen.
    // A silent 0 here is the exact failure this screen exists to stop.
    const decision = canPublish(radius, input.changeType, input.effectiveAt);
    if (!decision.ok) {
      setPublishError(decision.reason ?? null);
      return;
    }
    setBusy(true);
    setPublishError(null);
    try {
      await documentsApi.publish(documentId, open.versionNo, {
        effectiveAt: input.effectiveAt,
        changeType: input.changeType,
        changeSummary: input.changeSummary,
      });
      toastService.success(`v${open.versionNo} published, effective ${formatUtcDay(input.effectiveAt)}`);
      setPublishOpen(false);
      await load();
    } catch (e: any) {
      // `merge_fields_unresolvable`: the publish gate refused because at
      // least one written field cannot be filled in this document's context.
      // Each failure gets its own field name, reason (why), and remedy line
      // (what to do) — the same three-part shape as every other rejected
      // publish here, just repeated per field. `e.failures` is the array
      // `req()` in documentsApi.ts lifts off the 409 body; absent it (a
      // stale build of that helper, or a differently-shaped error) falls
      // back to the plain message like every other publish failure.
      const failures: PublishGateFailure[] | undefined = Array.isArray(e?.failures) ? e.failures : undefined;
      setPublishError(
        e?.message === 'merge_fields_unresolvable' && failures?.length
          ? formatGateFailures(failures)
          : e?.message || 'version_publish_failed',
      );
    } finally {
      setBusy(false);
    }
  }, [documentId, open, radius, load]);

  /* ── render ───────────────────────────────────────────────────────────── */

  if (loading && !detail) {
    return (
      <StaffPage section="Legal" tabs={<LegalNav />} icon={ScrollText} title="Document">
        <RubyLoader variant="page" label="Loading document…" className="min-h-[50vh]" />
      </StaffPage>
    );
  }

  if (error && !detail) {
    return (
      <StaffPage section="Legal" tabs={<LegalNav />} icon={ScrollText} title="Document">
        <div className="mx-auto max-w-xl py-12 text-center">
          <p className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            Could not load this document: <b>{error}</b>
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Button onClick={() => navigate(parentList.path)}>Back to {parentList.label}</Button>
            <Button variant="primary" onClick={() => void load()}>Retry</Button>
          </div>
        </div>
      </StaffPage>
    );
  }

  const doc = detail!.document;
  const subtitleBits: string[] = [];
  if (detail!.activeVersionNo) {
    subtitleBits.push(`Active version v${detail!.activeVersionNo}`);
    if (active?.effectiveAt) subtitleBits.push(`effective ${formatUtcDay(active.effectiveAt)}`);
  } else {
    subtitleBits.push('No version has taken effect yet');
  }
  if (open) subtitleBits.push(`editing v${open.versionNo} (${VERSION_STATUS_LABEL[open.status]})`);

  return (
    <StaffPage
      section="Legal"
      icon={ScrollText}
      title={doc.title}
      subtitle={(state ? `${state.label} · ` : '') + subtitleBits.join(' · ')}
      tabs={(
        <StaffSubtabs
          tabs={tabs.map((t) => ({ id: t.id, label: t.label }))}
          active={tab}
          onChange={(id) => setTab(id as TabId)}
        />
      )}
      actions={(
        <>
            <Button onClick={() => navigate(parentList.path)}>
              <ChevronLeft className="h-4 w-4" /> {parentList.label}
            </Button>
            <Button onClick={() => void load()} disabled={loading || busy}>
              {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </Button>

            {open?.status === 'draft' && (
              <>
                <Button onClick={() => void saveDraft()} disabled={!isDirty || saving}>
                  {saving ? 'Saving…' : isDirty ? 'Save draft' : 'Saved'}
                </Button>
                <Button
                  variant="admin"
                  disabled={busy || isDirty}
                  onClick={() => void runAction(
                    () => documentsApi.submit(documentId, open.versionNo),
                    `v${open.versionNo} submitted for review`,
                  )}
                >
                  Submit for review
                </Button>
              </>
            )}

            {open?.status === 'in_review' && (
              <>
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => { setSendBackReason(''); setSendBackFor(open); }}
                >
                  Reject…
                </Button>
                <Button
                  variant="admin"
                  disabled={busy}
                  onClick={() => void runAction(
                    () => documentsApi.approve(documentId, open.versionNo),
                    `v${open.versionNo} approved`,
                  )}
                >
                  Approve
                </Button>
              </>
            )}

            {open?.status === 'approved' && (
              <>
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => { setSendBackReason(''); setSendBackFor(open); }}
                >
                  Un-approve…
                </Button>
                <Button variant="primary" disabled={busy} onClick={openPublish}>
                  Publish v{open.versionNo}
                </Button>
              </>
            )}

            {!open && doc.status === 'active' && (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => void runAction(
                  () => documentsApi.createDraft(documentId),
                  'New version created from the active one',
                )}
              >
                New version
              </Button>
            )}
        </>
      )}
    >
      <div className="space-y-5">
        {open?.status === 'draft' && isDirty && (
          <WarnNote>
            Unsaved changes. <b>Save draft</b> before submitting for review — the reviewer
            reads what is stored, not what is on your screen.
          </WarnNote>
        )}

        {/* ── DOCUMENT — the published artifact ────────────────────────── */}
        {tab === 'document' && (
          <DocumentTab
            doc={doc}
            versions={versions}
            activeVersionNo={detail!.activeVersionNo}
            artifactsByVersion={artifactsByVersion}
            onNeedVersion={(n) => { void fetchBlocks(n).catch(() => null); }}
            onGoToEditor={() => setTab('editor')}
          />
        )}

        {/* ── EDITOR ───────────────────────────────────────────────────── */}
        {tab === 'editor' && (
          <EditorTab
            doc={doc}
            open={open}
            versions={versions}
            comparedNo={comparedNo}
            draftDelta={draftDelta}
            blocksByVersion={blocksByVersion}
            deltaByVersion={deltaByVersion}
            showChanges={showChanges}
            onShowChanges={setShowChanges}
            compareAgainst={compareAgainst}
            onCompareAgainst={(n) => {
              setCompareAgainst(n);
              // Fetched on demand: the chain can be long, and a comparison
              // nobody asked for is a read nobody needs.
              if (n !== null && deltaByVersion[n] === undefined && blocksByVersion[n] === undefined) {
                void fetchBlocks(n).catch(() => null);
              }
            }}
            onDeltaChange={(next) => { setDraftDelta(next); setDeltaDirty(true); }}
            onApiReady={(api) => { editorApi.current = api; }}
            onOpenSettings={() => setTab('settings')}
          />
        )}

        {/* ── VERSIONS ─────────────────────────────────────────────────── */}
        {tab === 'versions' && (
          <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
            <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
              <h3 className="border-b border-stone-200 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
                Version chain
              </h3>
              <div className="p-4">
                <VersionChain
                  versions={versions}
                  nowIso={nowIso}
                  selectedVersionNo={comparedNo ?? undefined}
                  onSelectVersion={(n) => setSelectedVersionNo(n)}
                />
              </div>
              <h3 className="border-y border-stone-200 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
                History — v{comparedNo}
              </h3>
              <div className="p-4">
                <VersionTimeline
                  entries={comparedNo ? auditByVersion[comparedNo] : null}
                />
              </div>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
              <h3 className="border-b border-stone-200 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
                {comparedPrevNo ? `Compare v${comparedPrevNo} → v${comparedNo}` : `Version v${comparedNo}`}
              </h3>
              <div className="p-4">
                {/*
                  * WHICH DIFF, AND WHY IT HAS TO BE DECIDED HERE.
                  *
                  * A continuous document stores `blocks: []`, so the block
                  * diff compared two empty arrays and rendered nothing at all
                  * — the version chain looked like it had no changes in it.
                  * The body a version actually has decides which comparison
                  * can say anything true about it.
                  */}
                {comparedNo && comparedPrevNo
                  && deltaByVersion[comparedNo] && deltaByVersion[comparedPrevNo] ? (
                    <DeltaCompare
                      before={deltaByVersion[comparedPrevNo]}
                      after={deltaByVersion[comparedNo]}
                      numbering={doc.numberingStyle ?? 'none'}
                    />
                  ) : comparedNo && comparedPrevNo
                    && (blocksByVersion[comparedNo]?.length || blocksByVersion[comparedPrevNo]?.length) ? (
                      <VersionDiff
                        prevBlocks={blocksByVersion[comparedPrevNo] ?? []}
                        nextBlocks={blocksByVersion[comparedNo] ?? []}
                        prevLabel={`v${comparedPrevNo} — ${versionCaption(versions, comparedPrevNo)}`}
                        nextLabel={`v${comparedNo} — ${versionCaption(versions, comparedNo)}`}
                      />
                    ) : comparedNo && comparedPrevNo ? (
                      <WarnNote>
                        v{comparedPrevNo} and v{comparedNo} cannot be compared word by word — one is a
                        continuous document and the other is a block document. Both still render and
                        publish normally.
                      </WarnNote>
                    ) : deltaByVersion[comparedNo ?? -1] ? (
                      <>
                        <p className="mb-3 text-[12.5px] text-stone-400">
                          v{comparedNo} has nothing before it to compare against — this is the document
                          as it stands.
                        </p>
                        <DeltaCompare
                          before={deltaByVersion[comparedNo ?? -1]}
                          after={deltaByVersion[comparedNo ?? -1]}
                          numbering={doc.numberingStyle ?? 'none'}
                        />
                      </>
                    ) : (
                      <>
                        <p className="mb-3 text-[12.5px] text-stone-400">
                          v{comparedNo} has nothing before it to compare against.
                        </p>
                        <BlockList blocks={blocksByVersion[comparedNo ?? -1] ?? []} />
                      </>
                    )}
              </div>
            </div>
          </div>
        )}

        {/* ── ACCEPTANCE — policies only; an agreement is signed, not accepted ── */}
        {tab === 'acceptance' && !isAgreement && (
          <div className="space-y-4 rounded-xl border border-stone-200 bg-white shadow-sm p-4">
            <h3 className="text-[12px] font-semibold uppercase tracking-wider text-stone-500">
              Acceptance
            </h3>
            <WarnNote>
              The acceptance ledger is written on the public surface, which is <b>Stage B</b>.
              Stage A only reads it, so every figure below is a real count against a table nothing
              has written to yet — 0 here means 0, not &ldquo;not implemented&rdquo;.
            </WarnNote>
            <dl className="grid gap-x-6 gap-y-2 text-[12.5px] sm:grid-cols-[190px_1fr]">
              <dt className="text-stone-500">Active version</dt>
              <dd className="text-stone-900">
                {detail!.activeVersionNo ? `v${detail!.activeVersionNo}` : 'none has taken effect'}
              </dd>
              <dt className="text-stone-500">Effective</dt>
              <dd className="text-stone-900">{formatUtcDay(active?.effectiveAt)}</dd>
              <dt className="text-stone-500">Change type of active</dt>
              <dd className="text-stone-900">
                {active?.changeType
                  ? <Tag tone={active.changeType === 'material' ? 'material' : 'minor'}>{active.changeType}</Tag>
                  : '—'}
              </dd>
              <dt className="text-stone-500">Summary shown to users</dt>
              <dd className="text-stone-700">{active?.changeSummary || '—'}</dd>
              <dt className="text-stone-500">In flight</dt>
              <dd className="text-stone-900">
                {open ? `v${open.versionNo} (${VERSION_STATUS_LABEL[open.status]})` : 'nothing'}
              </dd>
            </dl>
            {open && (
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={() => void loadRadius('material')} disabled={radius.loading}>
                  {radius.loading ? 'Checking…' : `Check blast radius of v${open.versionNo}`}
                </Button>
                {radius.forChangeType && !radius.loading && !radius.error && (
                  <span className="text-[12.5px] text-stone-400">
                    <b className={radius.count ? 'text-red-700' : 'text-stone-700'}>{radius.count}</b>
                    {' '}
                    of {radius.total} acceptance{radius.total === 1 ? '' : 's'} would be stale after
                    publishing v{open.versionNo} as <b>{radius.forChangeType}</b>
                    {radius.forEffectiveAt && radius.forEffectiveAt !== AT_NOW
                      ? <> effective {formatUtcDay(radius.forEffectiveAt)}</>
                      : <> today</>}
                    {' '}— absolute, including anyone already stale from an earlier material version.
                  </span>
                )}
                {radius.error && (
                  <span className="text-[12.5px] text-red-700">{radius.error}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── ENVELOPES — agreements only ──────────────────────────────── */}
        {tab === 'envelopes' && isAgreement && (
          <EnvelopesTab
            rows={envRows}
            details={envDetails}
            loading={envLoading}
            error={envError}
            openEnvelopeId={openEnvelopeId}
            onOpenEnvelope={setOpenEnvelopeId}
            onReload={() => void loadEnvelopes()}
          />
        )}

        {/* ── SETTINGS ─────────────────────────────────────────────────── */}
        {tab === 'settings' && (
          <SettingsTab
            documentId={documentId}
            document={doc}
            folders={folders}
            busy={busy}
            onSaved={() => void load()}
            onRetire={() => setRetireOpen(true)}
          />
        )}
      </div>

      {/*
        `staleAcceptanceCount` is passed ONLY when the fetched number answers the
        selection currently on screen (same change type, same effective date).
        Any other case leaves it undefined so the modal renders an explicit "—"
        rather than a 0 that would read as "this affects nobody".
      */}
      {sendBackFor && (
        <ModalShell
          labelledById="send-back-title"
          title={sendBackFor.status === 'approved'
            ? `Un-approve v${sendBackFor.versionNo}?`
            : `Reject v${sendBackFor.versionNo}?`}
          subtitle={sendBackFor.status === 'approved'
            ? 'Withdraws the approval and sends the version back to draft. Its approval record is '
              + 'kept — it really was approved, then withdrawn — and both events stay in the history.'
            : 'Sends the version back to draft so the author can revise it. The rejection and your '
              + 'reason are recorded permanently in the version history.'}
          onClose={() => setSendBackFor(null)}
          footerNote="The reason is mandatory and appears in the audit trail."
          footerActions={(
            <>
              <Button onClick={() => setSendBackFor(null)} disabled={busy}>Cancel</Button>
              <Button
                variant="danger"
                disabled={busy || !sendBackReason.trim()}
                onClick={() => void confirmSendBack()}
              >
                {busy
                  ? 'Working…'
                  : sendBackFor.status === 'approved'
                    ? `Un-approve v${sendBackFor.versionNo}`
                    : `Reject v${sendBackFor.versionNo}`}
              </Button>
            </>
          )}
        >
          <FieldLabel>Reason (shown to the author, kept in the audit trail)</FieldLabel>
          <textarea
            className={`${INPUT_CLASS} min-h-[96px] resize-y`}
            value={sendBackReason}
            onChange={(e) => setSendBackReason(e.target.value)}
            placeholder={sendBackFor.status === 'approved'
              ? 'e.g. Clause 4 changed in legal review — needs another pass before publishing'
              : 'e.g. Section 2 contradicts the pricing table; fix and resubmit'}
            autoFocus
          />
          {sendBackFor.status === 'approved' && (
            <WarnNote className="mt-3">
              This does not touch anything already published. Publishing stays permanent — a
              published version can only ever be superseded by a new version.
            </WarnNote>
          )}
        </ModalShell>
      )}

      {publishOpen && open && (
        <PublishModal
          documentTitle={doc.title}
          versionNo={open.versionNo}
          nowIso={nowIso}
          // Prefer the email snapshot — a UUID tells a reader nothing.
          approvedByName={open.approvedByEmail || open.approvedBy}
          approvedAtIso={open.approvedAt}
          changedBlockCount={changedBlockCount}
          staleAcceptanceCount={radiusCountFor(radius, pendingChangeType, pendingEffectiveAt)}
          blastRadiusDetail={(
            <RadiusDetail
              state={radius}
              changeType={pendingChangeType}
              hasDay={!!pendingDay}
              answersSelection={radiusAnswersSelection}
            />
          )}
          submitting={busy}
          error={publishError}
          onCancel={() => setPublishOpen(false)}
          onConfirm={(input) => void confirmPublish(input)}
          onChangeTypeChange={setPendingChangeType}
          onEffectiveDayChange={setPendingDay}
        />
      )}

      {retireOpen && (
        <ModalShell
          title={`Retire ${doc.title}?`}
          subtitle="Nothing is deleted. Every published version stays readable at its own URL forever."
          onClose={() => setRetireOpen(false)}
          footerNote="Retirement can only be reversed by a developer."
          footerActions={(
            <>
              <Button onClick={() => setRetireOpen(false)} disabled={busy}>Cancel</Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  setRetireOpen(false);
                  void runAction(() => documentsApi.retireDocument(documentId), 'Document retired');
                }}
              >
                Retire document
              </Button>
            </>
          )}
        >
          <DangerNote>
            The slug <b>/legal/{doc.slug}</b> is permanently spent. It can never be reused by
            another document, because the document id is derived from it.
          </DangerNote>
        </ModalShell>
      )}
    </StaffPage>
  );
}

/* ── the Document tab ───────────────────────────────────────────────────
 *
 * Shows THE PUBLISHED ARTIFACT, not a re-render of it.
 *
 * The stored HTML file is embedded directly, which is the whole point: its
 * SHA-256 is the hash bound to every signature, so what is on screen, what
 * prints, and what a signer agreed to are provably the same bytes. Rendering
 * a preview here instead would be a second implementation of the page design,
 * free to drift from the artifact without anything noticing.
 */
function DocumentTab({
  doc, versions, activeVersionNo, artifactsByVersion, onNeedVersion, onGoToEditor,
}: {
  doc: DocumentDetailPayload['document'];
  versions: readonly VersionSummary[];
  activeVersionNo: number | null;
  artifactsByVersion: Record<number, VersionArtifactRef[] | undefined>;
  onNeedVersion: (n: number) => void;
  onGoToEditor: () => void;
}) {
  /*
   * The version an operator means by "the document" is the one IN FORCE, not
   * merely the newest published one — a version published today to take
   * effect in January is not yet the agreement anybody signs.
   */
  const shown = activeVersionNo
    ?? [...versions].filter((v) => v.status === 'published')
      .sort((a, b) => b.versionNo - a.versionNo)[0]?.versionNo
    ?? null;

  useEffect(() => {
    if (shown !== null && artifactsByVersion[shown] === undefined) onNeedVersion(shown);
  }, [shown, artifactsByVersion, onNeedVersion]);

  const arts = shown === null ? undefined : artifactsByVersion[shown];
  const html = arts?.find((a) => a.format === 'html');
  const pdf = arts?.find((a) => a.format === 'pdf');
  const row = versions.find((v) => v.versionNo === shown);
  const frameRef = useRef<HTMLIFrameElement | null>(null);

  const print = useCallback(() => {
    // Printing the FRAME prints the artifact itself, headers and all — not a
    // screenshot of the admin page around it.
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    w.focus();
    w.print();
  }, []);

  if (shown === null) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <h3 className="text-[14px] font-semibold text-stone-900">Nothing published yet</h3>
        <p className="mt-2 max-w-[60ch] text-[12.5px] leading-relaxed text-stone-500">
          This document has no published version, so there is no artifact to print or send.
          A published version is frozen the moment it is created — that frozen file is what
          this tab shows, and what a signature binds to.
        </p>
        <Button className="mt-4" variant="primary" onClick={onGoToEditor}>Go to Edit</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm">
        <span className="text-[12.5px] text-stone-500">
          Showing <b className="text-stone-900">v{shown}</b>
          {row?.effectiveAt ? ` — effective ${formatUtcDay(row.effectiveAt)}` : ''}
          {row?.isActive ? ' · in force' : ''}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button onClick={print} disabled={!html?.url}>🖨 Print</Button>
          {pdf?.url && (
            <a
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 text-[13px] text-stone-700 hover:bg-stone-50"
              href={pdf.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              ⤓ Download PDF
            </a>
          )}
        </div>
      </div>

      {arts === undefined && (
        <RubyLoader variant="section" label="Loading the published document…" className="rounded-xl border border-stone-200 bg-white shadow-sm" />
      )}

      {arts && !html?.url && (
        <DangerNote>
          v{shown} is published but its stored HTML artifact could not be located. The version
          itself is unaffected — this is a problem reaching the file, not with the document.
        </DangerNote>
      )}

      {html?.url && (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <iframe
            ref={frameRef}
            title={`${doc.title} — v${shown}`}
            src={html.url}
            /* No `allow-scripts`: the artifact is static by design, and a
               legal document has no business running code. */
            sandbox="allow-same-origin allow-modals"
            className="h-[1100px] w-full border-0 bg-white"
          />
        </div>
      )}

      {html?.sha256 && (
        <p className="px-1 font-mono text-[10.5px] text-stone-400">
          sha256 {html.sha256}
        </p>
      )}
    </div>
  );
}

/* ── the Editor tab ─────────────────────────────────────────────────────
 *
 * The document is a PAGE, not a form: fixed measure, serif, real margins,
 * with the outline on one side and the fields and shortcuts on the other.
 * Everything about the layout exists to make the middle column feel like a
 * document somebody would want to type into.
 */

const RAIL_H = 'text-[10.5px] font-bold uppercase tracking-[0.09em] text-stone-400';

/**
 * OUTLINE DEPTH, and why it is remembered.
 *
 * A 13-section contract with sub-sections is a 40-row outline, which is not
 * an outline any more. The level buttons collapse it, and the choice is kept
 * in localStorage because an author works the same way on every document —
 * being made to re-collapse it on each one is the kind of small friction that
 * makes a panel go unused.
 *
 * This is a VIEW PREFERENCE and nothing else. It is per-browser, never sent
 * anywhere, and losing it costs a click.
 */
const OUTLINE_LEVEL_KEY = 'legal.outline.level';

function loadOutlineLevel(): number {
  try {
    const raw = localStorage.getItem(OUTLINE_LEVEL_KEY);
    const n = raw === null ? NaN : Number(raw);
    return n === 1 || n === 2 || n === 3 ? n : 3;
  } catch {
    // Private windows and blocked site data both throw here; the default view
    // is a perfectly good answer.
    return 3;
  }
}

/**
 * SHORTER NAMES FOR A TAB STRIP. The catalog's own labels are written for a
 * palette heading ("Our company", "Agreement & dates"); a tab has less room.
 * An override only — a group with no entry here uses its catalog label, so a
 * new group can never be missing a tab.
 */
const TAG_TAB_LABEL: Record<string, string> = {
  company: 'Company',
  agreement: 'Dates',
  finance: 'Finance',
  package: 'Package',
};

/**
 * Tag groups, as tabs. `null` is the "All" tab.
 *
 * DERIVED FROM THE FIELDS ACTUALLY OFFERED, not hand-listed. This was a
 * hardcoded list of eight groups, and when the catalog gained device, package,
 * vendor, employee and partner it kept showing the old eight — the fields
 * existed, the palette had them, and there was no tab to reach them by. A
 * second copy of the group list is exactly the drift the catalog exists to
 * prevent, so there is no longer a second copy.
 *
 * Groups with nothing to show in THIS document's contexts are omitted rather
 * than rendered empty: a Vendor tab on a hardware agreement is a dead end.
 */
function tagTabsFor(contexts?: readonly MergeContext[]): { id: string | null; label: string }[] {
  const present = new Set<string>(visibleFieldsForContexts(contexts).map((d) => d.group));
  const groups = MERGE_FIELD_GROUPS.filter((g) => present.has(g));
  return [
    { id: null, label: 'All' },
    ...groups.map((g) => ({
      id: g as string,
      label: TAG_TAB_LABEL[g] ?? MERGE_FIELD_GROUP_LABELS[g] ?? g,
    })),
  ];
}

function ShortcutRow({ label, keys, trigger }: {
  label: string; keys: string[]; trigger?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-[3px] text-[11.5px] leading-tight text-stone-700">
      <span>{label}</span>
      <span className="flex shrink-0 gap-0.5">
        {keys.map((k) => (
          <kbd
            key={k}
            className={`min-w-[17px] rounded border border-b-2 px-1 text-center font-mono text-[10.5px] leading-[1.4] ${
              trigger
                ? 'border-rose-200 bg-rose-50 font-extrabold text-rose-800'
                : 'border-stone-300 bg-white text-stone-700'
            }`}
          >
            {k}
          </kbd>
        ))}
      </span>
    </div>
  );
}

/** Cmd on a Mac, Ctrl on the Windows terminals the admin also runs on. */
const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '')
  ? '⌘' : 'Ctrl';

function ShortcutsRail() {
  return (
    <div className="mt-4 border-t border-stone-200 pt-3">
      <h4 className={RAIL_H}>Shortcuts</h4>
      <div className={`${RAIL_H} mt-2`}>Insert</div>
      <ShortcutRow label="Merge field" keys={['@']} trigger />
      <ShortcutRow label="Block" keys={['/']} trigger />
      <ShortcutRow label="Cross-reference" keys={['#']} trigger />
      <ShortcutRow label="Link" keys={[MOD, 'K']} />
      <div className={`${RAIL_H} mt-3`}>Format</div>
      <ShortcutRow label="Bold" keys={[MOD, 'B']} />
      <ShortcutRow label="Italic" keys={[MOD, 'I']} />
      <ShortcutRow label="Underline" keys={[MOD, 'U']} />
      <ShortcutRow label="Undo" keys={[MOD, 'Z']} />
      <ShortcutRow label="Redo" keys={[MOD, '⇧', 'Z']} />
      <div className={`${RAIL_H} mt-3`}>Menus</div>
      <ShortcutRow label="Move" keys={['↑', '↓']} />
      <ShortcutRow label="Insert" keys={['↵']} />
      <ShortcutRow label="Close" keys={['esc']} />
    </div>
  );
}

function EditorTab({
  doc, open, versions, comparedNo, draftDelta,
  blocksByVersion, deltaByVersion,
  showChanges, onShowChanges, compareAgainst, onCompareAgainst,
  onDeltaChange, onApiReady, onOpenSettings,
}: {
  doc: DocumentDetailPayload['document'];
  open: VersionSummary | null | undefined;
  versions: readonly VersionSummary[];
  comparedNo: number | null;
  draftDelta: DeltaDocument | null;
  blocksByVersion: Record<number, DocumentBlock[]>;
  deltaByVersion: Record<number, DeltaDocument | undefined>;
  showChanges: boolean;
  onShowChanges: (v: boolean) => void;
  compareAgainst: number | null;
  onCompareAgainst: (n: number | null) => void;
  onDeltaChange: (next: DeltaDocument) => void;
  onApiReady: (api: DeltaEditorApi | null) => void;
  /** Jumps to the Settings tab — the editor rail links to it rather than restating it. */
  onOpenSettings: () => void;
}) {
  const [api, setApi] = useState<DeltaEditorApi | null>(null);
  const [summary, setSummary] = useState<DeltaCompareSummary | null>(null);
  const [outlineLevel, setOutlineLevel] = useState<number>(() => loadOutlineLevel());
  const [tagTab, setTagTab] = useState<string | null>(null);

  /** Derived from the catalog and this document's contexts — never hand-listed. */
  const docContexts = doc.contexts as MergeContext[] | undefined;
  const contextKey = (docContexts ?? []).join('|');
  const tagTabs = useMemo(
    () => tagTabsFor(contextKey ? (contextKey.split('|') as MergeContext[]) : undefined),
    [contextKey],
  );

  // A tab can stop existing when the document's contexts narrow. Falling back
  // to "All" beats leaving the rail filtered to a group that is no longer
  // offered, which renders as an empty palette with no way back.
  useEffect(() => {
    if (tagTab && !tagTabs.some((t) => t.id === tagTab)) setTagTab(null);
  }, [tagTabs, tagTab]);

  const handleApi = useCallback((a: DeltaEditorApi | null) => {
    setApi(a);
    onApiReady(a);
  }, [onApiReady]);

  const isDraft = open?.status === 'draft';

  /** The body on screen: the live draft when editing, else the stored one. */
  const shownDelta = isDraft && draftDelta
    ? draftDelta
    : deltaByVersion[comparedNo ?? -1];

  /**
   * THE MODE, DERIVED FROM WHAT IS ON SCREEN — not from the saved row.
   *
   * An author who drops a signature block wants to see the document become
   * signed THERE, in the editor, not after a save and a trip to Settings. So
   * this counts the delta being edited, which is also what makes the conflict
   * appear the moment it is created.
   */
  const sigCount = useMemo(() => signatureBlockCount({ delta: shownDelta }), [shownDelta]);
  const liveDischarge = dischargeOf(doc as { kind?: string; discharge?: string }, sigCount > 0);
  const dischargeIssue = dischargeConflict(
    doc as { kind?: string; discharge?: string }, sigCount > 0, sigCount,
  );

  const outline = useMemo(
    () => deltaOutline(shownDelta, doc.numberingStyle ?? 'none'),
    [shownDelta, doc.numberingStyle],
  );

  /*
   * The ONLY case with no document to show: the blocks could not be converted
   * (see `asDelta`). That is a real failure and says so, rather than rendering
   * an empty page that looks like an empty contract.
   */
  if (!shownDelta) {
    const hasBlocks = (blocksByVersion[comparedNo ?? -1] ?? []).length > 0;
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        {hasBlocks ? (
          <DangerNote>
            This document could not be opened in the editor — its stored content could not be
            converted. Nothing is lost: the stored version is untouched and still renders. The
            console has the reason.
          </DangerNote>
        ) : (
          <p className="text-[12.5px] text-stone-400">
            This version is empty. {open?.status === 'draft'
              ? 'Start typing below to write it.'
              : 'Nothing was ever written to it.'}
          </p>
        )}
      </div>
    );
  }

  const compareDelta = compareAgainst !== null ? deltaByVersion[compareAgainst] : undefined;
  const comparable = versions
    .filter((v) => v.versionNo !== (open?.versionNo ?? comparedNo))
    .sort((a, b) => b.versionNo - a.versionNo);

  return (
    <>
      {/*
        WHAT KIND OF DOCUMENT THIS IS — one line, across the top of the tab
        people actually live on.

        Most authors never open Settings, so a mode that only shows there is a
        mode nobody knows is set — and this one decides what a reader is asked
        to DO. It reads live from the editor, so dropping in a signature block
        changes it immediately, and it links onward rather than restating the
        rest of Settings.
      */}
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-4 py-2.5 ${
          dischargeIssue
            ? 'border-amber-200 bg-amber-50'
            : liveDischarge === 'signature'
              ? 'border-emerald-100 bg-emerald-50/50'
              : 'border-sky-100 bg-sky-50/50'
        }`}
      >
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
            liveDischarge === 'signature' ? 'bg-emerald-600 text-white' : 'bg-sky-600 text-white'
          }`}
        >
          {liveDischarge === 'signature'
            ? <PenLine className="h-3.5 w-3.5" />
            : <CheckCircle2 className="h-3.5 w-3.5" />}
        </span>

        <span
          className={`text-[12.5px] font-semibold ${
            liveDischarge === 'signature' ? 'text-emerald-900' : 'text-sky-900'
          }`}
        >
          {liveDischarge === 'signature' ? 'Signature document' : 'Accept / Decline document'}
        </span>

        {/* Says WHY it is in this mode, so the label is not a mystery. */}
        <span className="text-[11.5px] text-stone-500">
          {doc.discharge
            ? 'Set on this document'
            : sigCount > 0
              ? 'Because it has a signature block'
              : `Default for ${doc.kind === 'agreement' ? 'an agreement' : 'a policy'}`}
        </span>

        {sigCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11.5px] text-stone-500">
            <FileSignature className="h-3 w-3 text-stone-400" />
            {sigCount} signature {sigCount === 1 ? 'block' : 'blocks'}
          </span>
        )}

        {dischargeIssue && (
          <span className="inline-flex items-start gap-1.5 text-[11.5px] leading-snug text-amber-800">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-600" />
            {dischargeIssue}
          </span>
        )}

        <button
          type="button"
          onClick={onOpenSettings}
          className="ml-auto inline-flex shrink-0 items-center gap-0.5 text-[11.5px] font-medium text-stone-600 underline-offset-2 transition hover:text-stone-900 hover:underline"
        >
          More settings
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

    <div className="grid gap-0 lg:grid-cols-[198px_minmax(0,1fr)_232px]">
      {/* ── outline ─────────────────────────────────────────────────── */}
      <aside className="hidden p-4 lg:block lg:border-r lg:border-stone-200">
        <div className="flex items-center justify-between gap-2">
          <h4 className={RAIL_H}>{showChanges ? 'Sections' : 'Outline'}</h4>
          <span className="text-[10px] text-stone-400">{outline.length} headings</span>
        </div>

        <div className="mt-2 flex items-center gap-1">
          <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-stone-400">Show</span>
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              type="button"
              title={`Show heading levels down to ${n}`}
              onClick={() => setOutlineLevel(n)}
              className={`flex h-[22px] min-w-[22px] items-center justify-center rounded-md border text-[11px] ${
                outlineLevel === n
                  ? 'border-admin-primary bg-admin-primary font-bold text-white'
                  : 'border-stone-300 bg-white text-stone-600 hover:border-stone-400'
              }`}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            title="Remember this depth for every document"
            onClick={() => {
              try { localStorage.setItem(OUTLINE_LEVEL_KEY, String(outlineLevel)); } catch { /* ignore */ }
              toastService.success(`Outline depth ${outlineLevel} saved as your default`);
            }}
            className="ml-auto text-[11px] text-stone-400 hover:text-stone-700"
          >
            📌 default
          </button>
        </div>

        <nav className="mt-2">
          {outline.filter((h) => h.level <= outlineLevel).map((h, i) => (
            <button
              key={`${h.anchorId ?? h.text}-${i}`}
              type="button"
              onClick={() => api?.goTo(h.index)}
              disabled={showChanges || !api}
              className={`block w-full rounded-md border-l-2 border-transparent px-2 py-1 text-left leading-snug hover:bg-white disabled:hover:bg-transparent ${
                h.level === 1 ? 'font-semibold text-stone-700'
                  : h.level === 2 ? 'text-stone-700' : 'pl-5 text-stone-500'
              } text-[12px]`}
            >
              {h.number && <span className="mr-1.5 tabular-nums text-stone-400">{h.number}</span>}
              {h.text || <span className="text-stone-300">Untitled</span>}
            </button>
          ))}
          {outline.length === 0 && (
            <p className="px-2 text-[11.5px] text-stone-400">
              No headings yet. Type <b>/</b> at the start of a line to add one.
            </p>
          )}
        </nav>
        {(doc.numberingStyle ?? 'none') === 'none' && outline.length > 0 && (
          <p className="mt-3 text-[10.5px] leading-snug text-stone-400">
            Section numbering is off for this document, so no numbers are printed.
            Turn it on in <b>Settings</b>.
          </p>
        )}
      </aside>

      {/* ── the page ────────────────────────────────────────────────── */}
      <div className="min-w-0 bg-stone-100 px-4 py-6">
        {isDraft && (
          <div className="mx-auto mb-4 flex w-[min(760px,100%)] flex-wrap items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2 shadow-sm">
            <div className="inline-flex overflow-hidden rounded-lg border border-stone-300">
              <button
                type="button"
                onClick={() => onShowChanges(false)}
                className={`px-3 py-1.5 text-[12px] ${!showChanges ? 'bg-admin-primary font-semibold text-white' : 'bg-white text-stone-500'}`}
              >
                Editing
              </button>
              <button
                type="button"
                onClick={() => onShowChanges(true)}
                className={`px-3 py-1.5 text-[12px] ${showChanges ? 'bg-admin-primary font-semibold text-white' : 'bg-white text-stone-500'}`}
              >
                Show changes
              </button>
            </div>

            {showChanges && (
              <div className="flex min-w-[250px] items-center gap-2 text-[12px] text-stone-500">
                against
                <div className="min-w-0 flex-1">
                  <SearchableDropdown
                    compact
                    value={compareAgainst === null ? null : String(compareAgainst)}
                    onChange={(v) => onCompareAgainst(v === null || v === '' ? null : Number(v))}
                    options={comparable.map((v) => ({
                      value: String(v.versionNo),
                      label: `v${v.versionNo} — ${versionCaption(versions, v.versionNo)}`,
                    }))}
                    placeholder="Pick a version…"
                  />
                </div>
              </div>
            )}

            {showChanges && summary && (
              <span className="ml-auto flex flex-wrap items-center gap-3 text-[11.5px] text-stone-500">
                <span className="font-semibold text-emerald-700">+{summary.wordsAdded}</span>
                <span className="font-semibold text-red-700">−{summary.wordsRemoved}</span>
                <span>{summary.sectionsTouched} of {summary.sectionsTotal} sections</span>
              </span>
            )}
          </div>
        )}

        {showChanges ? (
          compareAgainst === null ? (
            <p className="mx-auto w-[min(760px,100%)] text-[12.5px] text-stone-500">
              Pick a version to compare against.
            </p>
          ) : compareDelta === undefined ? (
            <WarnNote className="mx-auto w-[min(760px,100%)]">
              v{compareAgainst} is a <b>block document</b> and cannot be compared against a
              continuous one word by word. The <b>Versions</b> tab still shows its block diff.
            </WarnNote>
          ) : (
            <DeltaCompare
              before={compareDelta}
              after={shownDelta}
              numbering={doc.numberingStyle ?? 'none'}
              onSummary={setSummary}
            />
          )
        ) : (
          <DeltaEditor
            value={shownDelta}
            readOnly={!isDraft}
            numbering={doc.numberingStyle ?? 'none'}
            onChange={onDeltaChange}
            onApiReady={handleApi}
          />
        )}

        {!isDraft && (
          /*
           * A READ-ONLY EDITOR MUST SAY WHAT TO DO NEXT.
           *
           * Saying only "this cannot be edited" is a dead end: the version is
           * frozen for a real reason, but there IS a way forward in every
           * case, and the author has no way to know which button it is. Each
           * branch names the exact action that unfreezes the document.
           */
          <div className="mx-auto mt-3 w-[min(760px,100%)]">
            <WarnNote>
              {open?.status === 'approved' && (
                <>
                  <b>v{open.versionNo} is approved and frozen.</b> Its text cannot change between
                  approval and publishing — otherwise the version published would not be the one
                  that was approved. To change it, choose <b>Un-approve…</b> above: that sends it
                  back to draft and keeps both events in the history.
                </>
              )}
              {open?.status === 'in_review' && (
                <>
                  <b>v{open.versionNo} is in review and frozen.</b> The reviewer reads exactly what
                  was submitted. To change it, choose <b>Reject…</b> above to send it back to draft.
                </>
              )}
              {!open && (
                <>
                  <b>This is a published version.</b> Published versions are never edited — that is
                  what makes a signature verifiable years later. Choose <b>New version</b> above to
                  start the next version from this one.
                </>
              )}
            </WarnNote>
          </div>
        )}
      </div>

      {/* ── fields + shortcuts ──────────────────────────────────────── */}
      <aside className="hidden p-4 lg:block lg:border-l lg:border-stone-200">
        <h4 className={RAIL_H}>Tags</h4>
        {/* ONLY the tag list is tabbed — grouping keeps any one of them a
            single click away instead of scrolling past a hundred chips. */}
        <div className="mt-2 flex flex-wrap gap-0.5 border-b border-stone-200">
          {tagTabs.map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => setTagTab(t.id)}
              className={`rounded-t-md border-b-2 px-1.5 py-1 text-[10.5px] ${
                tagTab === t.id
                  ? 'border-admin-primary bg-white font-bold text-admin-primary'
                  : 'border-transparent text-stone-500 hover:text-stone-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-2">
          <MergeFieldPalette
            key={tagTab ?? 'all'}
            onlyGroup={tagTab ?? undefined}
            // The document's OWN contexts, so the palette offers only fields
            // that will survive its publish gate. Declaring none falls back to
            // the palette's permissive default, matching the gate's.
            contexts={docContexts}
            onInsert={(name) => api?.insertField(name)}
          />
        </div>
        <p className="mt-3 text-[10.5px] leading-snug text-stone-400">
          Click to drop one at the caret. <b>Faster:</b> type <b>@</b> in the document.
        </p>
        <ShortcutsRail />
      </aside>
    </div>
    </>
  );
}

function versionCaption(versions: readonly VersionSummary[], versionNo: number): string {
  const v = versions.find((x) => x.versionNo === versionNo);
  if (!v) return 'unknown';
  if (v.status !== 'published') return VERSION_STATUS_LABEL[v.status] || v.status;
  if (v.isActive) return 'published, active';
  if (v.isScheduled) return `published, effective ${formatUtcDay(v.effectiveAt)}`;
  return 'published, superseded';
}

/* ── envelopes tab ──────────────────────────────────────────────────────── */

/** `tenant_admin` → `tenant admin`. The stored value stays the stored value. */
const humanRole = (role: string): string => role.replace(/_/g, ' ');

/**
 * The signers cell.
 *
 * THREE OUTCOMES, THREE RENDERINGS. A detail read still in flight and one that
 * failed both show a dash, because neither is a number of signers. An envelope
 * that really has none shows so in amber — that is not a missing value, it is a
 * dead envelope: the service refuses to send an envelope addressed to nobody,
 * and there is no route to add a signer after creation, so it can never leave
 * draft. Rendering that as “0” in the same grey as everything else would bury
 * the one state on this screen that needs a human.
 */
function SignerCell({ detail }: { detail: EnvelopeDetail | null | undefined }) {
  if (detail === undefined) {
    return <span className="text-stone-400" title="Reading this envelope’s signers…">—</span>;
  }
  if (detail === null) {
    return (
      <span className="text-stone-400" title="This envelope’s signers could not be read. This is not zero signers.">
        —
      </span>
    );
  }
  const signers = detail.signers || [];
  if (!signers.length) {
    return (
      <span
        className="text-amber-600"
        title="An envelope must name at least one signer before it can be sent, and signers are only accepted at creation."
      >
        no signers
      </span>
    );
  }
  const signed = signers.filter((sg) => sg.status === 'signed').length;
  return (
    <div className="min-w-[150px]">
      <div className="text-stone-700">{signed} of {signers.length} signed</div>
      <div className="mt-0.5 text-[11px] text-stone-500">
        {signers.map((sg) => `${sg.name} (${humanRole(sg.role)})`).join(' · ')}
      </div>
    </div>
  );
}

/**
 * Mockup screen 5's envelope detail: what was frozen, and what has happened.
 *
 * The shared fields are read from the LIST ROW rather than from the stored
 * envelope on purpose. The two routes disagree about absence — the list
 * projection nulls every optional field, the detail route omits it — and
 * taking one side of that consistently is cheaper than testing for both
 * spellings at every call site. `mergeData`, the decline/void reasons and the
 * executed artifact exist only on the detail payload, so those come from there.
 */
/**
 * COUNTERSIGN — the platform's own §6.2 pass. The same mechanic as every
 * other surface: the served html IS the artifact the countersignature binds
 * to, scroll-to-end is a tracked fact, and the pad's consent checkbox is the
 * recorded consent. Rendered inline (ModalShell), no react-dom.
 */
function CountersignModal({
  envelopeId, onClose, onDone,
}: {
  envelopeId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [view, setView] = useState<Awaited<ReturnType<typeof documentsExtApi.getSigningView>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [consented, setConsented] = useState(false);
  const [busy, setBusy] = useState(false);
  const frameRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    let alive = true;
    documentsExtApi.getSigningView(envelopeId)
      .then((v) => { if (alive) setView(v); })
      .catch((e) => { if (alive) setError(e?.message || 'signing_view_unavailable'); });
    return () => { alive = false; };
  }, [envelopeId]);

  const onFrameLoad = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    if (!doc) return;
    const el = doc.scrollingElement || doc.documentElement;
    const check = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolled(true);
    };
    check();
    doc.addEventListener('scroll', check, { passive: true });
  }, []);

  const doSign = useCallback(async (result: SignatureResult) => {
    if (!view) return;
    setBusy(true);
    setError(null);
    try {
      const res = await documentsExtApi.countersign(envelopeId, {
        kind: result.kind,
        typedName: result.typedName,
        signatureImagePngBase64: result.signatureImagePngBase64,
        presentedSha256: view.presentedSha256,
        consentAccepted: consented,
        scrolledToEnd: scrolled,
      });
      toastService.success(res.executed
        ? 'Countersigned — the envelope is fully executed and the signed PDF is stored.'
        : 'Countersigned.');
      onDone();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'countersign_failed');
    } finally {
      setBusy(false);
    }
  }, [envelopeId, view, consented, scrolled, onDone, onClose]);

  return (
    <ModalShell
      labelledById="countersign-title"
      title={`Countersign — ${view?.title ?? envelopeId}`}
      subtitle="Review the exact document the other parties signed. Your signature binds to the rendered bytes on screen — the hash travels with it."
      onClose={onClose}
    >
      <div className="space-y-4">
        {error && (
          <DangerNote>{error}</DangerNote>
        )}
        <div className="h-[320px] overflow-hidden rounded-lg border border-stone-200 bg-white">
          {view ? (
            <iframe
              ref={frameRef}
              title="Agreement document"
              srcDoc={view.html}
              sandbox="allow-same-origin"
              onLoad={onFrameLoad}
              className="h-full w-full"
            />
          ) : (
            <RubyLoader variant="section" className="h-full" />
          )}
        </div>
        {view && view.signer && (
          <SignaturePad
            signerName={view.signer.name}
            scrolledToEnd={scrolled}
            busy={busy}
            onSign={doSign}
            onConsentChange={setConsented}
          />
        )}
        {view && !view.signer && (
          <WarnNote>This envelope has no platform signer to countersign as.</WarnNote>
        )}
      </div>
    </ModalShell>
  );
}

function EnvelopeDetailPanel({
  row, detail, onChanged,
}: {
  row: EnvelopeRow;
  detail: EnvelopeDetail | null | undefined;
  onChanged: () => void;
}) {
  const [countersignOpen, setCountersignOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadArtifact = useCallback(async () => {
    setDownloading(true);
    try {
      const { url } = await documentsExtApi.artifactUrl(row.envelopeId);
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      toastService.error(e?.message === 'artifact_not_ready'
        ? 'The signed PDF is not stored yet — the envelope has not executed.'
        : e?.message || 'artifact_unavailable');
    } finally {
      setDownloading(false);
    }
  }, [row.envelopeId]);
  if (detail === undefined) {
    return (
      <RubyLoader variant="section" label={`Reading envelope ${row.envelopeId}…`} className="rounded-xl border border-stone-200 bg-white shadow-sm" />
    );
  }
  if (detail === null) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-700">
        Envelope <b>{row.envelopeId}</b> could not be read. Nothing is known about its signers,
        its frozen merge data or its timeline — this is not an empty envelope.
      </div>
    );
  }

  const env = detail.envelope;
  const mergeEntries = Object.entries(env.mergeData || {});
  const events = detail.events || [];

  const platformSigner = (detail.signers || []).find(
    (s) => s.role === 'platform' && s.identityMethod === 'cognito_session',
  );
  const othersSigned = (detail.signers || [])
    .filter((s) => s.signerId !== platformSigner?.signerId)
    .every((s) => s.status === 'signed');
  const canCountersign = !!platformSigner
    && platformSigner.status !== 'signed'
    && (env.status === 'sent' || env.status === 'partially_signed');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {canCountersign && (
          <Button variant="primary" onClick={() => setCountersignOpen(true)}>
            Countersign{othersSigned ? ' — completes the envelope' : ''}…
          </Button>
        )}
        {env.status === 'executed' && (
          <Button onClick={() => void downloadArtifact()} disabled={downloading}>
            {downloading ? 'Preparing…' : 'Download signed PDF'}
          </Button>
        )}
      </div>

      {countersignOpen && (
        <CountersignModal
          envelopeId={row.envelopeId}
          onClose={() => setCountersignOpen(false)}
          onDone={onChanged}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
        <h3 className="border-b border-stone-200 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
          Detail
        </h3>
        <div className="space-y-4 p-4">
          <dl className="grid gap-x-6 gap-y-2 text-[12.5px] sm:grid-cols-[150px_1fr]">
            <dt className="text-stone-500">Envelope</dt>
            <dd className="break-all font-mono text-[12px] text-stone-900">{row.envelopeId}</dd>
            <dt className="text-stone-500">Template</dt>
            <dd className="text-stone-900">
              <span className="break-all font-mono text-[12px]">{row.templateDocumentId}</span>
              {' '}
              <b>@ v{row.templateVersionNo}</b>
              <span className="text-stone-500"> (frozen at creation — never &ldquo;latest&rdquo;)</span>
            </dd>
            <dt className="text-stone-500">Tenant</dt>
            <dd className="text-stone-700">{row.tenantId || 'none — platform-owned envelope'}</dd>
            <dt className="text-stone-500">Status</dt>
            <dd>
              <Tag tone={ENVELOPE_STATUS_TONE[row.status] || 'draft'}>
                {ENVELOPE_STATUS_LABEL[row.status] || row.status}
              </Tag>
            </dd>
            <dt className="text-stone-500">Created</dt>
            <dd className="text-stone-700">{formatUtcStamp(row.createdAt)} by {env.createdBy}</dd>
            <dt className="text-stone-500">Sent</dt>
            <dd className="text-stone-700">{row.sentAt ? formatUtcStamp(row.sentAt) : 'not sent'}</dd>
            <dt className="text-stone-500">Expires</dt>
            <dd className="text-stone-700">
              {row.expiresAt ? formatUtcDay(row.expiresAt) : 'no expiry until it is sent'}
            </dd>
            {row.executedAt && (
              <>
                <dt className="text-stone-500">Executed</dt>
                <dd className="text-emerald-600">{formatUtcStamp(row.executedAt)}</dd>
              </>
            )}
            {row.declinedAt && (
              <>
                <dt className="text-stone-500">Declined</dt>
                <dd className="text-red-700">
                  {formatUtcStamp(row.declinedAt)}
                  {env.declineReason ? ` — ${env.declineReason}` : ''}
                </dd>
              </>
            )}
            {row.voidedAt && (
              <>
                <dt className="text-stone-500">Voided</dt>
                <dd className="text-red-700">
                  {formatUtcStamp(row.voidedAt)}
                  {env.voidReason ? ` — ${env.voidReason}` : ''}
                </dd>
              </>
            )}
            {env.executedArtifact?.sha256 && (
              <>
                <dt className="text-stone-500">Executed artifact</dt>
                <dd className="break-all font-mono text-[11.5px] text-stone-700">
                  {env.executedArtifact.sha256}
                </dd>
              </>
            )}
          </dl>

          <div>
            <h4 className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-stone-500">
              Frozen merge data
            </h4>
            {mergeEntries.length === 0 ? (
              <p className="text-[12.5px] text-stone-500">
                Empty — and that is a legal answer, not a missing one: a template with no merge
                fields freezes nothing.
              </p>
            ) : (
              <dl className="grid gap-x-6 gap-y-1.5 text-[12.5px] sm:grid-cols-[150px_1fr]">
                {mergeEntries.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="font-mono text-[11.5px] text-stone-500">{k}</dt>
                    <dd className="break-words text-stone-700">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p className="mt-2 text-[11.5px] text-stone-500">
              Resolved once, when the envelope was created. If the tenant renames their business
              next year this agreement still reads exactly as it does today.
            </p>
          </div>

          <div>
            <h4 className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-stone-500">
              Signers
            </h4>
            {detail.signers.length === 0 ? (
              <p className="text-[12.5px] text-amber-600">
                None. An envelope must name at least one signer before it can be sent, and signers
                are only accepted at creation — so this one cannot leave draft.
              </p>
            ) : (
              <ul className="space-y-2">
                {detail.signers.map((sg) => (
                  <li
                    key={sg.signerId}
                    className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-[12.5px]"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-stone-900">{sg.name}</span>
                      <span className="text-stone-500">{humanRole(sg.role)}</span>
                      <Tag tone={sg.status === 'signed' ? 'published' : sg.status === 'declined' ? 'material' : 'draft'}>
                        {sg.status}
                      </Tag>
                    </div>
                    <div className="mt-1 text-[11.5px] text-stone-500">
                      {sg.email ? `${sg.email} · ` : ''}
                      identified by {humanRole(sg.identityMethod)}
                      {sg.signedAt ? ` · signed ${formatUtcStamp(sg.signedAt)}` : ''}
                      {sg.declinedAt ? ` · declined ${formatUtcStamp(sg.declinedAt)}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
        <h3 className="border-b border-stone-200 px-4 py-3 text-[12px] font-semibold uppercase tracking-wider text-stone-500">
          Timeline
        </h3>
        <div className="p-4">
          {events.length === 0 ? (
            <p className="text-[12.5px] text-stone-500">
              No events recorded for this envelope.
            </p>
          ) : (
            <ol className="space-y-3">
              {events.map((ev, i) => (
                <li
                  key={`${ev.at || 'na'}-${ev.action || 'na'}-${i}`}
                  className="border-l-2 border-stone-300 pl-3 text-[12.5px]"
                >
                  <div className="font-medium text-stone-900">{ev.action || 'unknown event'}</div>
                  <div className="text-[11.5px] text-stone-500">
                    {formatUtcStamp(ev.at)}
                    {ev.actorId ? ` · ${ev.actorId}` : ''}
                  </div>
                  {ev.detail && Object.keys(ev.detail).length > 0 && (
                    <div className="mt-1 break-words font-mono text-[11px] text-stone-500">
                      {JSON.stringify(ev.detail)}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
          <p className="mt-4 text-[11.5px] text-stone-500">
            Read from the append-only audit table. The service role can write and query it but
            cannot update or delete it, so this order is tamper-resistant at the permission layer
            rather than by convention. Event names are the stored spelling.
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

/**
 * LEGAL / AGREEMENTS / {template} / Envelopes — mockup screen 5.
 *
 * An envelope is ONE INSTANCE of ONE published version of this template,
 * addressed to named signers, with its merge data frozen. Two consequences
 * show up directly in this table and are not bugs:
 *
 *  • DIFFERENT ROWS LEGITIMATELY NAME DIFFERENT VERSIONS. The version is
 *    frozen when the envelope is created and is never resolved to “latest”,
 *    so publishing v5 leaves every existing envelope reading v4. A column that
 *    showed the template’s current version instead would be describing a
 *    document nobody signed.
 *
 *  • EXPIRY IS A DATE, NOT A GATE. `expiresAt` is stamped at send, 30 days
 *    out by default, but nothing sweeps it and nothing derives it at read time
 *    — so a past date here does NOT mean signing is blocked. It is presented
 *    as a date and claims nothing more.
 */
function EnvelopesTab({
  rows, details, loading, error, openEnvelopeId, onOpenEnvelope, onReload,
}: {
  rows: EnvelopeRow[] | null;
  details: Record<string, EnvelopeDetail | null>;
  loading: boolean;
  error: string | null;
  openEnvelopeId: string | null;
  onOpenEnvelope: (id: string | null) => void;
  onReload: () => void;
}) {
  const openRow = rows?.find((r) => r.envelopeId === openEnvelopeId) ?? null;

  return (
    <div className="space-y-4">
      <WarnNote>
        <b>Expiry is a date, not a gate.</b> An envelope is stamped with an expiry 30 days after
        it is sent, but nothing in the system enforces it yet — no sweeper marks an envelope
        expired and no check blocks a late signature. Treat the date as information, not as a
        closed door. <b>Version</b> is frozen per envelope: rows naming different versions of
        this template are correct, not stale.
      </WarnNote>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          Envelopes could not be listed: <b>{error}</b>. This is not &ldquo;no envelopes&rdquo; —
          nothing is known.
          <div className="mt-2">
            <Button onClick={onReload}>Retry</Button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 px-4 py-3">
          <h3 className="text-[12px] font-semibold uppercase tracking-wider text-stone-500">
            Envelopes from this template
          </h3>
          <Button onClick={onReload} disabled={loading}>
            {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-stone-50">
                {['Envelope', 'Status', 'Version', 'Signers', 'Created', 'Expires'].map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap border-b border-stone-200 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wider text-stone-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows === null && (
                <tr>
                  <td colSpan={6} className="px-3">
                    <RubyLoader variant="section" label="Listing envelopes…" />
                  </td>
                </tr>
              )}
              {!loading && rows !== null && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-[13px] text-stone-500">
                    No envelopes have been created from this template yet. That is a real zero —
                    the template is readable and nothing has been sent from it.
                  </td>
                </tr>
              )}
              {rows !== null && rows.map((r) => {
                const on = r.envelopeId === openEnvelopeId;
                return (
                  <tr
                    key={r.envelopeId}
                    onClick={() => onOpenEnvelope(on ? null : r.envelopeId)}
                    className={`cursor-pointer border-b border-stone-200 last:border-b-0 hover:bg-stone-50 ${
                      on ? 'bg-stone-100' : ''
                    }`}
                  >
                    <td className="px-3 py-3 align-middle">
                      <div className="font-mono text-[12px] text-stone-900">{r.envelopeId}</div>
                      <div className="mt-0.5 text-[11px] text-stone-400">
                        {on ? 'shown below' : 'open detail'}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <Tag tone={ENVELOPE_STATUS_TONE[r.status] || 'draft'}>
                        {ENVELOPE_STATUS_LABEL[r.status] || r.status}
                      </Tag>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 align-middle text-[13px] text-stone-700">
                      v{r.templateVersionNo}
                    </td>
                    <td className="px-3 py-3 align-middle text-[13px]">
                      <SignerCell detail={details[r.envelopeId]} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 align-middle text-[13px] text-stone-700">
                      {formatUtcStamp(r.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 align-middle text-[13px] text-stone-700">
                      {r.expiresAt ? formatUtcDay(r.expiresAt) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openRow && (
        <EnvelopeDetailPanel
          row={openRow}
          detail={details[openRow.envelopeId]}
          onChanged={onReload}
        />
      )}

      <div className="rounded-xl border border-stone-200 bg-white shadow-sm p-4 text-[12.5px] leading-relaxed text-stone-400">
        This list is <b className="text-stone-800">one page</b> of the platform’s envelopes,
        newest first, filtered to this template after the read — so on a busy account it can
        under-report. <b className="text-stone-800">Signers</b> are read one envelope at a time
        because the list projection does not carry them; a dash there means the read has not
        landed or failed, never that the envelope has no signers. Timestamps are shown in{' '}
        <b className="text-stone-800">UTC</b>, matching the audit trail they came from.
      </div>
    </div>
  );
}

/* ── settings tab ───────────────────────────────────────────────────────── */

function SettingsTab({
  documentId, document: doc, folders, busy, onSaved, onRetire,
}: {
  documentId: string;
  document: { title: string; slug: string; kind: string; folderId: string; category?: string; contexts?: string[]; status: string; createdAt: string; retiredAt?: string; system?: boolean };
  folders: readonly Folder[];
  busy: boolean;
  onSaved: () => void;
  onRetire: () => void;
}) {
  const [title, setTitle] = useState(doc.title);
  const [folderId, setFolderId] = useState<string | null>(doc.folderId);
  const [category, setCategory] = useState<string | null>(doc.category ?? null);
  /**
   * WHAT this document is for. Drives the publish gate: with contexts declared,
   * a merge field whose provider the context does not carry is rejected at
   * publish rather than discovered by a customer. Declaring none keeps the
   * permissive default, which is why every existing document still publishes.
   */
  const [contexts, setContexts] = useState<MergeContext[]>((doc.contexts as MergeContext[]) ?? []);
  const [saving, setSaving] = useState(false);

  // Only what this document's own kind is offered under — an agreement is never
  // offered a policy category.
  const { options: categoryChoices, loading: categoriesLoading } =
    useDocumentCategories(doc.kind === 'agreement' ? 'agreement' : 'policy');

  const savedContexts = useMemo(() => (doc.contexts as MergeContext[]) ?? [], [doc.contexts]);

  /**
   * HOW THIS DOCUMENT IS DISCHARGED. `dischargeOf` supplies what the kind used
   * to imply for every document written before the field existed, so the
   * control opens on the behaviour the document already has rather than on a
   * blank the operator has to guess at.
   */
  const savedDischarge = dischargeOf(doc as { kind?: string; discharge?: string });
  const [discharge, setDischarge] = useState<DocumentDischarge>(savedDischarge);

  useEffect(() => {
    setTitle(doc.title);
    setFolderId(doc.folderId);
    setCategory(doc.category ?? null);
    setContexts(savedContexts);
    setDischarge(savedDischarge);
  }, [doc.title, doc.folderId, doc.category, savedContexts, savedDischarge]);

  const categoryChanged = (category ?? null) !== (doc.category ?? null);
  // Order is not meaningful, so compare as sets — re-ticking the same two
  // contexts in a different order is not a change to save.
  const contextsChanged = contexts.length !== savedContexts.length
    || contexts.some((c) => !savedContexts.includes(c));
  const dischargeChanged = discharge !== savedDischarge;
  const changed = title.trim() !== doc.title || folderId !== doc.folderId
    || categoryChanged || contextsChanged || dischargeChanged;

  const save = async () => {
    if (!changed || !title.trim()) return;
    setSaving(true);
    try {
      await documentsApi.updateDocument(documentId, {
        title: title.trim() !== doc.title ? title.trim() : undefined,
        folderId: folderId && folderId !== doc.folderId ? folderId : undefined,
        // Recategorising is free — a signature binds a VERSION, never a filing
        // decision. Clearing one is deliberately not offered: a document that
        // should appear nowhere is retired, not un-categorised.
        ...(categoryChanged && category ? { category } : {}),
        // Only sent when non-empty: the PATCH route takes a non-empty list, and
        // clearing back to "no contexts" is a return to the permissive default
        // rather than a value to write.
        ...(contextsChanged && contexts.length ? { contexts } : {}),
        // Changing this changes how the NEXT envelope presents the document.
        // Envelopes already created froze their own copy, so nothing in front
        // of a signer changes under them.
        ...(dischargeChanged ? { discharge } : {}),
      });
      toastService.success('Document updated');
      onSaved();
    } catch (e: any) {
      toastService.error(e?.message || 'document_update_failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 rounded-xl border border-stone-200 bg-white shadow-sm p-4">
      <h3 className="text-[12px] font-semibold uppercase tracking-wider text-stone-500">Settings</h3>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <FieldLabel requirement="required">Title</FieldLabel>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={INPUT_CLASS} />
        </div>
        <div>
          <FieldLabel>Folder</FieldLabel>
          <SearchableDropdown
            options={folderOptions(folders)}
            value={folderId}
            onChange={setFolderId}
            placeholder="Choose a folder"
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            How you file it. Sub-folders are shown nested, and nothing in the product reads this.
          </p>
        </div>
        <div>
          <FieldLabel>Category</FieldLabel>
          <SearchableDropdown
            options={categoryChoices}
            value={category}
            onChange={setCategory}
            placeholder={categoriesLoading ? 'Loading categories…' : 'Where is this offered?'}
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Where the product OFFERS it — the axis every picker filters on. Without one this
            document stays in the library and appears nowhere.{' '}
            <a href="#/legal/categories" className="underline hover:text-stone-700">Manage categories</a>
          </p>
        </div>
      </div>

      {/*
        HOW IT IS DISCHARGED — the control that lets a document be signed OR
        answered, whatever it is filed as.

        Before this, `kind` decided: an agreement was signed, a policy was
        accepted on a different surface entirely, and neither could be the
        other. That is why a checkout could not put its paperwork in one place.
        Setting this is what lets every document ride in the same envelope.
      */}
      <div>
        <FieldLabel>How it is discharged</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {([
            {
              value: 'signature' as const,
              label: 'Signature document',
              hint: 'The reader signs it. Their signature binds to the exact bytes they were shown.',
              icon: <PenLine className="h-3.5 w-3.5" />,
              on: 'bg-emerald-600 text-white',
            },
            {
              value: 'accept_decline' as const,
              label: 'Accept / Decline document',
              hint: 'The reader is shown it and answers. A decline is recorded as a real answer.',
              icon: <CheckCircle2 className="h-3.5 w-3.5" />,
              on: 'bg-sky-600 text-white',
            },
          ]).map((opt) => {
            const on = discharge === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                title={opt.hint}
                onClick={() => setDischarge(opt.value)}
                className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-medium transition ${
                  on ? opt.on : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {opt.icon}
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11.5px] text-stone-500">
          {discharge === 'signature'
            ? 'The reader signs it, and it anchors the envelope it travels in.'
            : 'This document is ANSWERED, not signed — it still travels in the same envelope as everything else on the order, and still has to be opened before the packet can be completed.'}
          {dischargeChanged && (
            <span className="ml-1 text-amber-700">
              Applies to the NEXT envelope. Anything already sent froze its own copy and does not change.
            </span>
          )}
        </p>
      </div>

      <div>
        <FieldLabel>Used for</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {MERGE_CONTEXTS.map((ctx) => {
            const on = contexts.includes(ctx);
            return (
              <button
                key={ctx}
                type="button"
                onClick={() => setContexts((prev) => (
                  prev.includes(ctx) ? prev.filter((c) => c !== ctx) : [...prev, ctx]
                ))}
                className={`min-h-[36px] rounded-lg border px-3 text-[12px] font-medium transition ${
                  on
                    ? 'border-rose-300 bg-rose-50 text-rose-700'
                    : 'border-stone-300 text-stone-600 hover:border-stone-400'
                }`}
              >
                {MERGE_CONTEXT_LABELS[ctx]}
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11.5px] text-stone-500">
          What the document is ABOUT — checked at publish. A merge field whose data does not
          exist in one of these subjects is refused here, rather than rendering blank in front
          of a signer. Naming none leaves the check permissive, which is how every document
          written before this field still publishes.
        </p>
        {contexts.length > 0 && (
          <p className="mt-1 text-[11.5px] text-amber-700">
            Narrowing this can make the NEXT publish fail if the document already uses a field
            these subjects do not carry. Nothing already published is affected.
          </p>
        )}
      </div>

      <dl className="grid gap-x-6 gap-y-2 text-[12.5px] sm:grid-cols-[190px_1fr]">
        <dt className="text-stone-500">Slug</dt>
        <dd className="font-mono text-[11.5px] text-stone-700">/legal/{doc.slug}</dd>
        <dt className="text-stone-500">Document id</dt>
        <dd className="font-mono text-[11.5px] text-stone-700">{documentId}</dd>
        <dt className="text-stone-500">Kind</dt>
        <dd className="text-stone-700">{doc.kind}</dd>
        <dt className="text-stone-500">Created</dt>
        <dd className="text-stone-700">{formatUtcStamp(doc.createdAt)}</dd>
        {doc.retiredAt && (
          <>
            <dt className="text-stone-500">Retired</dt>
            <dd className="text-stone-700">{formatUtcStamp(doc.retiredAt)}</dd>
          </>
        )}
      </dl>

      <WarnNote>
        The slug is immutable. It is a permanent public URL and the document id is derived from it,
        so it can never be changed and never be reused — even after retirement.
      </WarnNote>

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => void save()} disabled={!changed || saving || busy}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        {/* A SYSTEM document can never be retired (the service answers 409
            `system_document_locked`), so the control is replaced, not offered. */}
        {doc.status !== 'retired' && !doc.system && (
          <Button variant="danger" onClick={onRetire} disabled={busy}>Retire document</Button>
        )}
        {doc.system && (
          <span
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-[#0A0A0D] px-3 text-[12px] font-semibold text-white"
            title="Built-in master document — it cannot be retired. Change it with a new version."
          >
            System document · locked
          </span>
        )}
      </div>
    </div>
  );
}
